<script setup lang="ts">
import {
  Calendar,
  Fold,
  InfoFilled,
  StarFilled,
} from "@element-plus/icons-vue";
import { CollapseModelValue, ElMessage, ElMessageBox } from "element-plus";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import type {
  HousingFinderInput,
  IntakeScenarioEntryDto,
  TreeNodeDto,
  Workflow,
  WorkflowContentDto,
} from "~/api";
import {
  ApiError,
  ContactActivityDto,
  FormDefinition,
  FormResponse,
  FormResponseAnswerDto,
  FormResponseDto,
  FormResponseStatus,
  FormType,
  HousingApplication,
  HousingApplicationActivityCode,
  HousingApplicationLogDto,
  HousingApplicationSource,
  HousingApplicationStatus,
  HousingIntakeScenario,
  HousingIntakeScenarioProcessedApplicationStatus,
  HousingIntakeScenarioVacancy,
  HousingSiteResult,
  HousingSiteResultType,
  LinkedPersonDto,
  LinkedWorkflowSummaryDto,
  WorkflowDto,
  WorkflowStatus,
} from "~/api";
import type { HousingApplicationDto } from "~/api/generated/models/HousingApplicationDto";
import ContactActivityTable from "~/Compliance/components/ContactActivityTable.vue";
import WorkflowResponseViewer from "~/Compliance/components/WorkflowResponseViewer.vue";
import {
  useWorkflow,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import CRMEntityDetail from "~/Compliance/pages/CRMEntityDetail.vue";
import FormResponseTemplate from "~/Compliance/pages/FormResponseTemplate.vue";
import { customActionButtonInjectKey } from "~/NewFormBuilder/composables/useCustomActionButton";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { applicationRouteMap } from "~/router/mainRouter";
import { getEnvConfig } from "~/services/appConfig";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { getMessageTime, splitByCaps } from "~/utils/miscUtils";
import { ApplicationDrawerTabs } from "~/YYC_Housing/HMB/types/hmbEnums";
import { housingApplicationStatusDisplayNamesHMBSite } from "~/YYC_Housing/utils/enumDisplayNames";
import {
  contactHistoryActivityTypeOptions,
  formatIntakeScenarioName,
} from "~/YYC_Housing/utils/hmbUtils";
import ApplicationLocations from "./ApplicationLocations.vue";
import ApplicationNote from "./ApplicationNote.vue";
import AssignTo from "./AssignTo.vue";
import DateWithTimePopover from "./DateWithTimePopover.vue";
import HousingApplicationFinderViewer from "./HousingApplicationFinderViewer.vue";
import IntakeActionPanel from "./IntakeActionPanel.vue";

import EnumTag from "~/components/EnumTag.vue";
import LinkedWorkflowsTable from "./LinkedWorkflowsTable.vue";

import { HubConnection } from "@microsoft/signalr";
import _ from "lodash";
import { buildTreeNodeMap } from "~/composables/useGeoTree";
import { useSectorTree } from "~/composables/useSectorTree";
import { useUnitTypeTree } from "~/composables/useUnitTypeTree";
import { getUnansweredQuestionCount } from "~/NewFormBuilder/composables/useFormResponse/getUnansweredQuestionCount";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { emptyGuid } from "~/utils/constants";
import { formatDate } from "~/utils/dateUtils";
import { useLiveHousingApplicationManager } from "~/YYC_Housing/HMB/composables/useLiveHousingApplicationManager";
import { useHousingSiteStore } from "~/YYC_Housing/stores/HousingSiteStore";
import { useMyHousingOrgRolesStore } from "~/YYC_Housing/stores/MyHousingOrgRolesStore";
import { ACCESSIBLE_LEGACY_ATTR_GUID } from "../../utils/constants";
import RefreshButton from "../components/RefreshButton.vue";
import HousingApplicationOfflineSection from "./HousingApplicationOfflineSection.vue";

const housingApplicationStatusOrder: Record<HousingApplicationStatus, number> =
  {
    [HousingApplicationStatus.DRAFT]: 0,
    [HousingApplicationStatus.NEW]: 1,
    [HousingApplicationStatus.SUBMITTED]: 1,
    [HousingApplicationStatus.IN_REVIEW]: 2,
    [HousingApplicationStatus.ASSIGNED]: 2,
    [HousingApplicationStatus.PRSCOMPLETED]: 3,
    [HousingApplicationStatus.WAITLIST]: 4,
    [HousingApplicationStatus.WAITLISTED]: 4,
    [HousingApplicationStatus.ON_HOLD]: 4,
    [HousingApplicationStatus.INTAKE]: 5,
    [HousingApplicationStatus.LOCKED_INTERVIEWING]: 6,
    [HousingApplicationStatus.OFFER_ACTIVE]: 7,
    [HousingApplicationStatus.LOCKED_WITH_OFFER]: 6, // legacy, same as LOCKED_INTERVIEWING
    [HousingApplicationStatus.OFFER_ACCEPTED]: 8,
    [HousingApplicationStatus.OFFER_REJECTED]: 8,
    [HousingApplicationStatus.HOUSED]: 9,
    [HousingApplicationStatus.CANCELLED]: 10,
    [HousingApplicationStatus.CANCELED]: 10,
  };

const statusAtLeast = (
  current: HousingApplicationStatus | undefined,
  threshold: HousingApplicationStatus,
): boolean => {
  if (!current) return false;
  return (
    (housingApplicationStatusOrder[current] ?? -1) >=
    housingApplicationStatusOrder[threshold]
  );
};

const props = defineProps<{
  // Accept either the lightweight list DTO or the full HousingApplication; the component
  // will fetch full details on mount / when the prop changes.
  housingApp: HousingApplication | HousingApplicationDto;
  drawerOpen: boolean;
  cascadeFilter?: string[];
  reviewFitScenario?: HousingIntakeScenario;
  waitlistScenario?: HousingIntakeScenario;
  // Subset of the waitlist scenario's vacancies the applicant is eligible for,
  // resolved from the waitlist entry's matchedVacancyGuids.
  matchedVacancies?: HousingIntakeScenarioVacancy[];
  // Target vacancy group for StartIntake. Required for multi-group V2 scenarios so
  // UnitsFilled counts can be scoped per-group; omitted for legacy or single-group
  // scenarios (server falls back to auto-select).
  reviewFitVacancyGuid?: string | null;
  // Pre-loaded Intake Process WorkflowDto from the waitlist entry, if available.
  // Used to render the inline workflow without an extra fetch.
  intakeWorkflow?: WorkflowDto;
  // True when this applicant is the next eligible for Review Fit on the waitlist.
  isNextReviewFit?: boolean;
  // Initial tab to open when the drawer opens
  initialTab?: ApplicationDrawerTabs;
}>();

const emit = defineEmits<{
  (e: "drawerClosed", finalApp?: HousingApplication): void;
  (e: "updateHousingApp", newApp: HousingApplication): void;
  (e: "refetch:reviewFitScenario"): void;
}>();

const loginAccountsStore = useLoginAccountsStore();
const housingSiteStore = useHousingSiteStore();
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();
const myHousingOrgRolesStore = useMyHousingOrgRolesStore();
const { loadUnitTypeTree, getNodeName: getUnitTypeNodeName } =
  useUnitTypeTree();
const { loadSectorTree, getNodeName: getSectorNodeName } = useSectorTree();

const buildingTypeMap = ref(new Map<string, TreeNodeDto>());
const buildingAttrMap = ref(new Map<string, TreeNodeDto>());
const loadBuildingTrees = async () => {
  await myInfoStore.load();
  const org = myInfoStore.currentSysOrgGuid;
  const [bt, ba] = await Promise.all([
    apiClient.tree
      .getTreeDefinitionByType(org, "BuildingType")
      .catch(() => null),
    apiClient.tree
      .getTreeDefinitionByType(org, "BuildingAttribute")
      .catch(() => null),
  ]);
  buildingTypeMap.value.clear();
  buildTreeNodeMap(bt?.nodes ?? [], buildingTypeMap.value);
  buildingAttrMap.value.clear();
  buildTreeNodeMap(ba?.nodes ?? [], buildingAttrMap.value);
};
const getBuildingTypeName = (guid: string) =>
  buildingTypeMap.value.get(guid)?.name;
const getBuildingAttrName = (guid: string) =>
  buildingAttrMap.value.get(guid)?.name;

const router = useRouter();
const isViteDevelopment = getEnvConfig().env === "development";

const localHousingApp = ref<HousingApplication>();
const timeline = ref<HousingApplicationLogDto[]>([]);
const usersForms = ref<FormResponseDto[]>([]);
const scenario = ref<HousingIntakeScenario>();
const intakeScenarioEntries = ref<IntakeScenarioEntryDto[]>([]);
const activeIntakeScenarioEntries = computed(() => {
  const activeStatuses = new Set<
    HousingIntakeScenarioProcessedApplicationStatus | undefined | null
  >([
    null,
    undefined,
    HousingIntakeScenarioProcessedApplicationStatus.INTERVIEWING,
    HousingIntakeScenarioProcessedApplicationStatus.INTAKE, // legacy status
    HousingIntakeScenarioProcessedApplicationStatus.OFFER_IN_PROGRESS,
    HousingIntakeScenarioProcessedApplicationStatus.ACCEPTED,
  ]);
  return intakeScenarioEntries.value.filter((e) =>
    activeStatuses.has(e.status),
  );
});

const lockedInScenarioEntry = computed<IntakeScenarioEntryDto | undefined>(
  () => {
    return intakeScenarioEntries.value.find(
      (e) =>
        e.status ===
          HousingIntakeScenarioProcessedApplicationStatus.INTERVIEWING ||
        e.status === HousingIntakeScenarioProcessedApplicationStatus.INTAKE || // legacy status
        e.status ===
          HousingIntakeScenarioProcessedApplicationStatus.OFFER_IN_PROGRESS ||
        e.status === HousingIntakeScenarioProcessedApplicationStatus.ACCEPTED,
    );
  },
);

const overrideHousingFinderInputs = ref<HousingFinderInput>();
const finderDrawerOpen = ref(false);
const finderDrawerReadOnly = ref(false);

const hasLegacyAccessibleAttr = computed(() =>
  overrideHousingFinderInputs.value?.buildingAttributes?.some(
    (g) => g.toLowerCase() === ACCESSIBLE_LEGACY_ATTR_GUID,
  ),
);

// Contact detail drawer state
const selectedContact = ref<LinkedPersonDto | null>(null);
const contactDrawerVisible = ref(false);
const answersFromHousingApplication = ref<FormResponseAnswerDto[]>([]);
const linkedPeople = ref<LinkedPersonDto[]>([]);
const linkedWorkflows = ref<LinkedWorkflowSummaryDto[]>([]);
const selectedWorkflow = ref<WorkflowDto | null>(null);
const selectedWorkflowFocusFormResponseGuid = ref<string | null>(null);
const intakeWorkflowLocal = ref<WorkflowDto | null>(null);
const activeIntakeWorkflow = computed<WorkflowDto | undefined>(
  () => intakeWorkflowLocal.value ?? props.intakeWorkflow ?? undefined,
);
const housingAppWorkflowInstance = ref<Workflow>();
const housingAppWorkflowContent = ref<WorkflowContentDto>();

const newOccupancyDate = ref<string>();

/** True when the modal is open */
const showAddNoteDialog = ref<boolean>(false);
/** The note open in the modal */
const noteBeingEdited = ref<FormResponseDto>();

const selectedLocationsExpanded = ref<boolean>(false);
const notesExpanded = ref<boolean>(true);

// Draft checklist sync: cache form definition and track re-render key
const draftChecklistFormDef = ref<FormDefinition>();
const draftChecklistSyncKey = ref(0);
const draftChecklistFullResponse = ref<FormResponse>();

const prsChecklistFormResponse = computed<FormResponse | undefined>(() => {
  const workflow = housingAppWorkflowContent.value?.workflow;
  if (!workflow) return undefined;
  const formDef = workflow.workflowDefinition?.formDefinitions?.find(
    (fd) => fd.name === "PRS Review Checklist",
  );
  if (!formDef) return undefined;
  return (
    workflow.formResponses?.find(
      (fr) => fr.formDefinitionGuid === formDef.guid,
    ) ?? undefined
  );
});

const draftChecklistFormResponse = computed<FormResponse | undefined>(() => {
  const workflow = housingAppWorkflowContent.value?.workflow;
  if (!workflow) return undefined;
  const formDef = workflow.workflowDefinition?.formDefinitions?.find(
    (fd) => fd.name === "Draft Checklist",
  );
  if (!formDef) return undefined;
  return (
    workflow.formResponses?.find(
      (fr) => fr.formDefinitionGuid === formDef.guid,
    ) ?? undefined
  );
});

provide(customActionButtonInjectKey, {
  handleCustomAction: async (code: string) => {
    switch (code) {
      case "open-finder":
        handleOpenFinder(false);
        break;
      case "open-application-form":
        selectedWorkflowFocusFormResponseGuid.value =
          housingAppWorkflowContent.value?.workflow.formResponses?.find(
            (fr) =>
              fr.formDefinitionGuid === "9c32c468-e138-4ec2-8ce6-6de26c596099", // TODO: this is the form definition guid of the prs form
          )?.guid ?? null;

        console.log(
          "Opening application form with guid: " +
            selectedWorkflowFocusFormResponseGuid.value,
        );
        await openApplicationWorkflow();
        break;
    }
  },
});

const interactionWorkflowNames = new Set([
  "CRM Interaction Log",
  "Interaction Log",
  "Legacy Interaction",
  "Contact Activity",
  "On Hold",
]);

const vacancyManagementWorkflowDefintionGuids = new Set([
  "1C8A63B6-BE44-F111-8EF3-002248AEBDFE", //	1 Strike Form
  "5C8BC5F6-BE44-F111-8EF3-002248AEBDFE", //	3 Strike Form
  "15EDA758-BF44-F111-8EF3-002248AEBDFE", //	No Offer Form
  "B95150C6-BF44-F111-8EF3-002248AEBDFE", //	Cancel No Offer Form
  "B039E65F-C044-F111-8EF3-002248AEBDFE", //	Offer Form
  "A856ACA3-2D76-4BC0-92D9-44DC6C978F2B", //	Intake Process
  "454056A6-B722-F111-9A49-6045BD5B8A82", //	Legacy: Offer
  "36D12DC4-B722-F111-9A49-6045BD5B8A82", //	Legacy: No Offer
]);

const nonInteractionWorkflows = computed<LinkedWorkflowSummaryDto[]>(() =>
  linkedWorkflows.value.filter(
    (w) =>
      !w.workflowArchivedAt &&
      !interactionWorkflowNames.has(w.workflowDefinitionName) &&
      !vacancyManagementWorkflowDefintionGuids.has(
        w.workflowDefinitionGuid.toUpperCase(),
      ),
  ),
);

const currentProcessedStatus = computed<string | null>(() => {
  const scenario = props.reviewFitScenario ?? props.waitlistScenario;
  if (!scenario) return null;
  const appGuid = props.housingApp?.guid ?? localHousingApp.value?.guid;
  const entry = scenario.processedHousingApplicationEntries?.find(
    (e) => e.applicationGuid === appGuid,
  );
  return entry?.status ?? null;
});

const _isIntakeScenarioRejected = computed<boolean>(() => {
  if (!props.reviewFitScenario) {
    return false;
  }
  return props.reviewFitScenario.processedHousingApplicationEntries.some(
    (entry) =>
      entry.applicationGuid ===
        (props.housingApp?.guid ?? localHousingApp.value?.guid) &&
      entry.status === HousingIntakeScenarioProcessedApplicationStatus.REJECTED,
  );
});

const notes = computed(() =>
  usersForms.value
    .filter(
      (f) =>
        f.formType === FormType.APPLICATION_NOTE &&
        f.status === FormResponseStatus.SUBMITTED,
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
);

const currentSectionsOpened = computed(() => {
  const sections = [
    //always expanded
    ApplicationDrawerTabs.APPSUM,
  ];

  // Add initial tab if specified
  if (props.initialTab && !sections.includes(props.initialTab)) {
    sections.push(props.initialTab);
  }

  if (
    props.housingApp.status === HousingApplicationStatus.NEW ||
    props.housingApp.status === HousingApplicationStatus.IN_REVIEW
  ) {
    sections.push(ApplicationDrawerTabs.NEWANDPRS, ApplicationDrawerTabs.FORMS);
  } else if (
    props.housingApp.status === HousingApplicationStatus.DRAFT &&
    props.housingApp.source === HousingApplicationSource.PAPER_APPLICATION
  ) {
    sections.push(ApplicationDrawerTabs.FORMS);
    sections.push(ApplicationDrawerTabs.OFFLINEAPPS);
  } else if (
    props.reviewFitScenario ||
    props.waitlistScenario ||
    props.housingApp.status === HousingApplicationStatus.LOCKED_INTERVIEWING ||
    props.housingApp.status === HousingApplicationStatus.HOUSED ||
    props.housingApp.status === HousingApplicationStatus.LOCKED_WITH_OFFER
  ) {
    sections.push(ApplicationDrawerTabs.INTAKEANDOFFER);
  } else if (props.housingApp.status === HousingApplicationStatus.WAITLIST) {
    sections.push(ApplicationDrawerTabs.WAITLIST);
  }
  return sections;
});
const originalSectionsOpened = ref(currentSectionsOpened.value);

// Reactive state for open sections
const openedSections = ref(originalSectionsOpened.value);

const locationsSelectedOriginal = computed<HousingSiteResult[]>(
  () =>
    localHousingApp.value?.siteResultsOriginal?.filter(
      (r) => r.resultType === HousingSiteResultType.PREFERRED,
    ) ?? [],
);

const locationsSelectedOverride = computed<HousingSiteResult[]>(
  () =>
    localHousingApp.value?.siteResults?.filter(
      (r) => r.resultType === HousingSiteResultType.PREFERRED,
    ) ?? [],
);

const locationsSelectedHasOverride = computed<boolean>(() => {
  const overrideKeys = new Set(
    locationsSelectedOverride.value.map((r) => `${r.hmb}|${r.siteCode}`),
  );
  const originalKeys = new Set(
    locationsSelectedOriginal.value.map((r) => `${r.hmb}|${r.siteCode}`),
  );
  if (overrideKeys.size !== originalKeys.size) return true;
  for (const key of overrideKeys) {
    if (!originalKeys.has(key)) return true;
  }
  return false;
});
// const locationsQualified = computed<HousingSiteResult[]>(
//   () =>
//     localHousingApp.value?.siteResults?.filter(
//       (r) => r.resultType === HousingSiteResultType.QUALIFIED,
//     ) ?? [],
// );

const applicationRejectedThreeOffers = computed<boolean>(() => {
  return (
    timeline.value.filter(
      (l) => l.activityCode === HousingApplicationActivityCode.OFFER_REJECTED,
    ).length >= 3
  );
});
const createdByUser = computed<string>(() => {
  return localHousingApp.value?.createdByUser?.loginName ?? "-";
});

// ── Vacancy Management helpers ──

const matchedVacanciesVisible = ref(false);

const intakeDisplayScenario = computed<HousingIntakeScenario | undefined>(
  () => props.reviewFitScenario ?? scenario.value ?? props.waitlistScenario,
);

const intakeVacancyByMonth = computed(() => {
  const vacancies = [...(intakeDisplayScenario.value?.vacancies ?? [])].sort(
    (a, b) => a.dateAvailable.localeCompare(b.dateAvailable),
  );
  const grouped = new Map<string, number>();
  for (const v of vacancies) {
    const d = new Date(v.dateAvailable);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const avail = Math.max(0, (v.totalUnits ?? 0) - (v.unitsFilled ?? 0));
    grouped.set(key, (grouped.get(key) ?? 0) + avail);
  }
  const months: { key: string; label: string; available: number }[] = [];
  for (const [key, available] of grouped) {
    const [y, m] = key.split("-");
    const label = `${new Date(+y, +m - 1).toLocaleString("default", { month: "short" })} ${y}`;
    months.push({ key, label, available });
  }
  return months;
});

const intakeTotalUnits = computed(() =>
  (intakeDisplayScenario.value?.vacancies ?? []).reduce(
    (s, v) => s + (v.totalUnits ?? 0),
    0,
  ),
);
const intakeTotalFilled = computed(() =>
  (intakeDisplayScenario.value?.vacancies ?? []).reduce(
    (s, v) => s + (v.unitsFilled ?? 0),
    0,
  ),
);

const intakeEarliestAvailableDate = computed(() => {
  const sorted = [...(intakeDisplayScenario.value?.vacancies ?? [])].sort(
    (a, b) => a.dateAvailable.localeCompare(b.dateAvailable),
  );
  const withUnits = sorted.find(
    (v) => (v.totalUnits ?? 0) - (v.unitsFilled ?? 0) > 0,
  );
  return withUnits?.dateAvailable ?? sorted[0]?.dateAvailable ?? null;
});

const intakeOfferFormatUnitType = (unitType: string | undefined) => {
  switch (unitType) {
    case "s":
      return "Studio";
    case "1":
      return "1 Bedroom";
    case "2":
      return "2 Bedroom";
    case "supports":
      return "Studio - With Supports";
    default:
      return unitType ?? "—";
  }
};

const intakeNoOfferCount = ref(0);
const intakeStrikesCount = ref(0);

const handleWorkflowCounts = (counts: {
  noOffers: number;
  strikes: number;
}) => {
  intakeNoOfferCount.value = counts.noOffers;
  intakeStrikesCount.value = counts.strikes;
};

const intakeContactActivityCount = computed<number>(() => {
  return linkedWorkflows.value.filter(
    (w) => w.workflowDefinitionName === "Contact Activity",
  ).length;
});

const intakeContactActivitySinceLockedCount = computed<number>(() => {
  const lockedDate = localHousingApp.value?.dateLockedForIntake
    ? new Date(localHousingApp.value.dateLockedForIntake)
    : null;
  if (!lockedDate) {
    return intakeContactActivityCount.value;
  }
  return linkedWorkflows.value.filter(
    (w) =>
      w.workflowDefinitionName === "Contact Activity" &&
      new Date(w.linkedAt) >= lockedDate,
  ).length;
});

// const handleViewAllContactActivities = () => {
//   ElMessage.info("View All Contact Activities — Work in Progress");
// };

// const handleViewContactActivitiesSinceLocked = () => {
//   ElMessage.info("View Contact Activities Since Locked — Work in Progress");
// };

const handleClose = async () => {
  await stopPresenceConnection();
  currentPresenceGuid = null;
  isCurrentUserIdle.value = false;
  controlRequestPending.value = false;
  if (idleTimer) clearTimeout(idleTimer);

  // Clean up our hub listeners + leave workflow group (safe; does not stop shared conns)
  await leaveHub();

  emit("drawerClosed", localHousingApp.value);
};

const _isConfirmedReadyForWaitlist = ref(false);

const _isMoveToWaitlistDisabled = computed<boolean>(() => {
  return (
    !localHousingApp.value?.reviewedApplicationFormsHMBUser ||
    !localHousingApp.value?.reviewedPropertySelectionsHMBUser ||
    !localHousingApp.value?.interviewCompletedHMBUser
  );
});

const timelineLoaded = ref(false);
const getApplicationTimeLine = async (applicationGuid: string) => {
  await apiClient.housingApplicationLog
    .getApplicationLogs(applicationGuid)
    .then((res) => {
      timeline.value = res;
      timelineLoaded.value = true;
    });
};

const _handleGetUsersForms = async (applicationGuid: string) => {
  await apiClient.housingApplication
    .getResponses(applicationGuid)
    .then((formResponseDtos) => {
      usersForms.value = formResponseDtos;
    });
};

const handleOpenFinder = (isReadOnly: boolean) => {
  finderDrawerReadOnly.value = isReadOnly;
  finderDrawerOpen.value = true;
};
// const formatUnitType = (unitType: string) => {
//   switch (unitType) {
//     case "s":
//       return "Studio";
//     case "1":
//       return "1 Bedroom";
//     case "2":
//       return "2 Bedroom";
//     default:
//       return unitType;
//   }
// };

const formatSource = (applicationSource: string) => {
  switch (applicationSource) {
    case "PaperApplication":
      return "Created By HMB Staff";
    case "SeniorApplication":
      return "Senior Application";
    case "DataImport":
      return "Data Import";
    default:
      return applicationSource;
  }
};

const setStatus = async (status: HousingApplicationStatus) => {
  if (localHousingApp.value) {
    await apiClient.housingApplication
      .setStatus(localHousingApp.value?.guid, status)
      .then((dto) => {
        localHousingApp.value = dto;
        ElMessage.success(`Status set to ${status}`);
      })
      .catch((e: ApiError) => {
        showError(e);
      });
  } else {
    showError("Housing Application is null or undefined");
  }
};

const setScore = async () => {
  if (localHousingApp.value) {
    await apiClient.housingApplication
      .updateHousingApplication(
        localHousingApp.value?.guid,
        localHousingApp.value,
      )
      .then((dto) => {
        localHousingApp.value = dto;
        emit("updateHousingApp", dto);
      })
      .catch((e: ApiError) => {
        showError(e);
      });
  } else {
    showError("Housing Application is null or undefined");
  }
};

const handleCancelApplication = async () => {
  // Check for existing open cancel workflow
  const existingCancelWorkflow = linkedWorkflows.value.find(
    (w) =>
      w.workflowDefinitionName === "Cancel Application" &&
      !w.workflowArchivedAt &&
      !w.isWorkflowComplete,
  );
  if (existingCancelWorkflow) {
    await openWorkflow(existingCancelWorkflow.workflowGuid);
    return;
  }

  if (!localHousingApp.value?.entityGuid) {
    showError("Application has no linked entity.");
    return;
  }
  if (!myInfoStore.currentSysOrg?.guid) {
    showError("No current organization.");
    return;
  }

  const applicantName = localHousingApp.value.applicantName;

  ElMessageBox.confirm(
    `Are you sure you want to create a "Cancel" workflow for ${applicantName} and cancel their application?`,
    `Cancel Application For: ${applicantName}`,
  )
    .then(async () => {
      try {
        const newWorkflow =
          await apiClient.workflowInstance.createWorkflowWithLinks({
            workflowDefinitionName: "Cancel Application",
            orgGuid: myInfoStore.currentSysOrgGuid,
            primaryEntityGuid: localHousingApp.value!.entityGuid!,
            linkedEntities: [
              {
                entityGuid: localHousingApp.value!.entityGuid!,
                linkType: "HousingApplication",
              },
            ],
            createContactActivity: true,
            housingApplicationEntityGuid: localHousingApp.value!.entityGuid!,
          });

        ElMessage.success(`Cancel workflow created for ${applicantName}`);

        await loadLinkedWorkflows(localHousingApp.value!.entityGuid!);
        selectedWorkflow.value = newWorkflow;
      } catch (error) {
        showError(error);
      }
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const _handleRequestInformation = async () => {
  showError("Information request forms are now created via workflows.");
};

const handlePlaceOnHold = async () => {
  if (!localHousingApp.value?.guid) {
    showError(
      "Developer Error: cannot place on hold if no application provided",
    );
    return;
  }
  if (!localHousingApp.value?.entityGuid) {
    showError("Application has no linked entity.");
    return;
  }
  if (!myInfoStore.currentSysOrg?.guid) {
    showError("No current organization.");
    return;
  }

  try {
    // Create the workflow instance
    const newWorkflow =
      await apiClient.workflowInstance.createWorkflowWithLinks({
        workflowDefinitionName: "On Hold",
        orgGuid: myInfoStore.currentSysOrgGuid,
        primaryEntityGuid: localHousingApp.value.entityGuid,
        linkedEntities: [
          {
            entityGuid: localHousingApp.value.entityGuid,
            linkType: "HousingApplication",
          },
        ],
        createContactActivity: true,
        housingApplicationEntityGuid: localHousingApp.value.entityGuid,
      });

    ElMessage.success(
      `"On Hold" workflow created for ${localHousingApp.value.applicantName}`,
    );

    selectedWorkflow.value = newWorkflow;

    // Refresh linked workflows so the active On Hold workflow is visible immediately
    await loadLinkedWorkflows(localHousingApp.value.entityGuid);
  } catch (error) {
    showError(error);
  }
};

const handleEditOrRemoveOnHold = async () => {
  const workflow = activeOnHoldWorkflow.value;
  if (!workflow) {
    showError("No active On Hold workflow found.");
    return;
  }
  await openWorkflow(workflow.workflowGuid);
};

const handleMoveBackToReview = async () => {
  if (!localHousingApp.value?.guid) {
    showError(
      "Developer Error: cannot move back to review if no application provided",
    );
    return;
  }
  if (!localHousingApp.value?.entityGuid) {
    showError("Application has no linked entity.");
    return;
  }
  if (!myInfoStore.currentSysOrg?.guid) {
    showError("No current organization.");
    return;
  }

  // Check for existing open Move Back To Review workflow
  const existingWorkflow = linkedWorkflows.value.find(
    (w) =>
      w.workflowDefinitionName === "Move Back To Review" &&
      !w.workflowArchivedAt &&
      !w.isWorkflowComplete,
  );
  if (existingWorkflow) {
    await openWorkflow(existingWorkflow.workflowGuid);
    return;
  }

  try {
    const newWorkflow =
      await apiClient.workflowInstance.createWorkflowWithLinks({
        workflowDefinitionName: "Move Back To Review",
        orgGuid: myInfoStore.currentSysOrgGuid,
        primaryEntityGuid: localHousingApp.value.entityGuid,
        linkedEntities: [
          {
            entityGuid: localHousingApp.value.entityGuid,
            linkType: "HousingApplication",
          },
        ],
        createContactActivity: true,
        housingApplicationEntityGuid: localHousingApp.value.entityGuid,
      });

    ElMessage.success(
      `"Move Back To Review" workflow created for ${localHousingApp.value.applicantName}`,
    );

    selectedWorkflow.value = newWorkflow;
    await loadLinkedWorkflows(localHousingApp.value.entityGuid);
  } catch (error) {
    showError(error);
  }
};

const _updateOccupancyDate = async () => {
  if (newOccupancyDate.value === undefined) {
    ElMessage({
      type: "info",
      message: "Please select a date",
    });
    return;
  }

  await apiClient.housingApplication
    .updateHousingApplication(
      (props.housingApp?.guid ?? localHousingApp.value?.guid)!,
      {
        ...localHousingApp.value,
        occupancyDate: newOccupancyDate.value.toString(),
      },
    )
    .then((app) => {
      localHousingApp.value = app;
      emit("updateHousingApp", app);
      ElMessage.success(`Occupancy date updated to ${newOccupancyDate.value}`);
    })
    .catch(showError);
};

const updateEarliestOccupancyDate = async (newVal?: Date) => {
  const newValDateOnly = newVal?.toISOString().split("T")[0] ?? null;
  await apiClient.housingApplication
    .updateHousingApplication(
      (props.housingApp?.guid ?? localHousingApp.value?.guid)!,
      {
        ...localHousingApp.value,
        earliestOccupancyDate: newValDateOnly,
      },
    )
    .then((app) => {
      localHousingApp.value = app;
      emit("updateHousingApp", app);
      if (newVal) {
        ElMessage.success(
          `Earliest Occupancy Date Updated to ${newValDateOnly}`,
        );
      } else {
        ElMessage.warning(`Earliest Occupancy Date Removed`);
      }
    })
    .catch(showError);
};

const handleHistoryOfEventsShow = async () => {
  if (!localHousingApp.value?.guid) {
    showError("No application found. Cannot show timeline.");
    return;
  }
  if (!timelineLoaded.value) {
    await getApplicationTimeLine(localHousingApp.value?.guid);
  }
};

const dumpInfo = computed(() => {
  return JSON.stringify(localHousingApp.value, null, 2);
});

const daysInReview = computed(() => {
  if (!localHousingApp.value || !localHousingApp.value.dateSubmitted) {
    return "-";
  }
  const submittedDate = new Date(localHousingApp.value.dateSubmitted);
  const today = new Date();

  const dateDifference =
    (today.getTime() - submittedDate.getTime()) / (1000 * 60 * 60 * 24);

  return Math.floor(dateDifference);
});

const daysWaiting = computed(() => {
  if (!localHousingApp.value || !localHousingApp.value.dateWaitlisted) {
    return "-";
  }
  const waitlistedDate = new Date(localHousingApp.value.dateWaitlisted);
  const today = new Date();

  const dateDifference =
    (today.getTime() - waitlistedDate.getTime()) / (1000 * 60 * 60 * 24);

  return Math.floor(dateDifference);
});

// ── Linked People (Contacts) ──

const sortedLinkedPeople = computed(() => {
  if (!linkedPeople.value.length) return [];
  const order: Record<string, number> = {
    applicant: 0,
    "co-applicant": 1,
    coapplicant: 1,
    "alt contact": 2,
    "alternate contact": 2,
    agent: 3,
  };
  return [...linkedPeople.value].sort((a, b) => {
    const aRole = (a.contactType || a.entityDefinitionName || "").toLowerCase();
    const bRole = (b.contactType || b.entityDefinitionName || "").toLowerCase();
    const aOrder = order[aRole] ?? 99;
    const bOrder = order[bRole] ?? 99;
    return aOrder - bOrder;
  });
});

const _primaryContact = computed(() =>
  linkedPeople.value.find((p) => p.isPrimaryContact),
);

const editingContactSection = ref(false);
const contactInfoDraft = ref("");
const contactInfoChanged = computed(
  () => contactInfoDraft.value !== (localHousingApp.value?.contactInfo ?? ""),
);

const contactActivityDefinitionGuid = computed(() => {
  return (
    orgInfoStore.MyOrgInfo?.workflowDefinitionDtos.find(
      (wd) => wd.name === "Contact Activity",
    )?.guid ?? null
  );
});

// ── Contact History ──
const contactHistoryActivities = ref<ContactActivityDto[]>([]);
const contactHistoryStatusList = ref<WorkflowStatus[]>([]);
const contactHistoryLoading = ref(false);
const contactHistoryLoaded = ref(false);
const drawerDataLoaded = ref(false);

// ── Concurrency / Presence ──
const {
  startConnection: startPresenceConnection,
  stopConnection: stopPresenceConnection,
  viewerMap: presenceViewerMap,
  applicationEditors,
  onControlRequestReceived,
  onControlRequestResponse,
  requestApplicationControl,
  respondToControlRequest,
  reportIdle,
  reportActive,
} = useLiveHousingApplicationManager();

const controlRequestPending = ref(false);
const isCurrentUserIdle = ref(false);
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivityReset = 0;
let currentPresenceGuid: string | null = null;
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const resetIdleTimer = () => {
  const now = Date.now();
  if (now - lastActivityReset < 1000) return;
  lastActivityReset = now;

  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    const guid = localHousingApp.value?.guid;
    if (guid) {
      isCurrentUserIdle.value = true;
      reportIdle(guid);
    }
  }, IDLE_TIMEOUT_MS);
};

const canResumeEditing = computed(() => {
  const currentUserName = myInfoStore.userInfo?.loginName;
  const activeOthers = applicationEditors.value.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );
  return activeOthers.length === 0;
});

const resumeEditing = () => {
  const guid = localHousingApp.value?.guid;
  if (!guid || !isCurrentUserIdle.value) return;
  if (!canResumeEditing.value) return;
  isCurrentUserIdle.value = false;
  reportActive(guid);
  resetIdleTimer();
};

const handleDrawerActivity = () => {
  if (isCurrentUserIdle.value) {
    resumeEditing();
  } else {
    resetIdleTimer();
  }
};

const effectiveReadOnly = computed(() => {
  if (isCurrentUserIdle.value) return true;

  const editors = applicationEditors.value;
  const currentUserName = myInfoStore.userInfo?.loginName;
  const otherActiveUsers = editors.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );

  if (otherActiveUsers.length === 0) return false;

  // First-in-wins: earliest non-idle user gets edit access
  const currentUser = editors.find((u) => u.userName === currentUserName);
  if (!currentUser) return true;

  const currentJoined = new Date(currentUser.joinedAt);
  const isEarliest = otherActiveUsers.every(
    (u) => !u.joinedAt || new Date(u.joinedAt) > currentJoined,
  );
  return !isEarliest;
});

const currentEditorName = computed(() => {
  if (!effectiveReadOnly.value) return null;
  if (isCurrentUserIdle.value) return null;

  const currentUserName = myInfoStore.userInfo?.loginName;
  const activeOthers = applicationEditors.value.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );
  if (activeOthers.length === 0) return null;

  const sorted = [...activeOthers].sort(
    (a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime(),
  );
  return sorted[0]?.userName ?? null;
});

const idleEditorName = computed(() => {
  const currentUserName = myInfoStore.userInfo?.loginName;
  const activeOthers = applicationEditors.value.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );
  if (activeOthers.length === 0) return null;
  const sorted = [...activeOthers].sort(
    (a, b) => new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime(),
  );
  return sorted[0]?.userName ?? null;
});

const presenceViewers = computed(() => {
  const guid = localHousingApp.value?.guid;
  if (!guid) return [];
  return presenceViewerMap.value[guid] ?? [];
});

const presenceOtherViewers = computed(() => {
  const currentUserName = myInfoStore.userInfo?.loginName;
  return presenceViewers.value.filter((v) => v !== currentUserName);
});

onUnmounted(() => {
  if (idleTimer) clearTimeout(idleTimer);
  // Fire-and-forget safe cleanup (handlers + Leave group). Avoid await in onUnmounted.
  leaveHub();
});

const onHoldDefinitionGuid = computed(() => {
  return (
    orgInfoStore.MyOrgInfo?.workflowDefinitionDtos.find(
      (wd) => wd.name === "On Hold",
    )?.guid ?? null
  );
});

const activeOnHoldWorkflow = computed<LinkedWorkflowSummaryDto | undefined>(
  () => {
    return linkedWorkflows.value.find(
      (wf) =>
        wf.workflowDefinitionName === "On Hold" &&
        !wf.workflowArchivedAt &&
        wf.workflowStatus !== "OnHoldRemoved",
    );
  },
);

const onHoldDueDate = ref<string | undefined>();

const formattedOnHoldDueDate = computed(() => {
  const raw = onHoldDueDate.value;
  if (!raw) return undefined;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return raw;
  // Example: "Mar 27, 2026, 9:00 AM" (uses user's locale)
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
});

const canMoveBackToReview = computed(() => {
  const disallowedStatuses = new Set([
    HousingApplicationStatus.ON_HOLD,
    HousingApplicationStatus.DRAFT,
    HousingApplicationStatus.NEW,
    HousingApplicationStatus.IN_REVIEW,
    HousingApplicationStatus.INTAKE,
    HousingApplicationStatus.OFFER_ACTIVE,
    HousingApplicationStatus.OFFER_ACCEPTED,
    HousingApplicationStatus.LOCKED_INTERVIEWING,
    HousingApplicationStatus.LOCKED_WITH_OFFER,
    HousingApplicationStatus.HOUSED,
  ]);
  return (
    !activeOnHoldWorkflow.value &&
    localHousingApp.value?.status !== undefined &&
    !disallowedStatuses.has(localHousingApp.value.status)
  );
});

const relativeOnHoldDueDate = computed(() => {
  const raw = onHoldDueDate.value;
  if (!raw) return undefined;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return undefined;

  const diffSeconds = Math.round((dt.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);

  if (abs < 60)
    return diffSeconds >= 0 ? "in a few seconds" : "a few seconds ago";
  const diffMinutes = Math.round(diffSeconds / 60);
  if (Math.abs(diffMinutes) < 60)
    return diffMinutes > 0
      ? `in ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`
      : `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) > 1 ? "s" : ""} ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24)
    return diffHours > 0
      ? `in ${diffHours} hour${diffHours > 1 ? "s" : ""}`
      : `${Math.abs(diffHours)} hour${Math.abs(diffHours) > 1 ? "s" : ""} ago`;
  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30)
    return diffDays > 0
      ? `in ${diffDays} day${diffDays > 1 ? "s" : ""}`
      : `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? "s" : ""} ago`;

  // fallback to a short formatted date if it's further out
  return formattedOnHoldDueDate.value;
});

const loadOnHoldDueDate = async () => {
  const workflow = activeOnHoldWorkflow.value;
  if (!workflow) {
    onHoldDueDate.value = undefined;
    return;
  }

  try {
    // Get the full workflow details to access the answers
    const fullWorkflow = await apiClient.workflowInstance.getWorkflow(
      workflow.workflowGuid,
    );
    const formResponses = fullWorkflow.workflow.formResponses ?? [];

    // Search all formResponses for an answer with the TaskActivityDate question code
    let answer = undefined as any;
    for (const fr of formResponses) {
      if (!fr.answers) continue;
      const a = fr.answers.find(
        (x: any) => x.formElement?.questionCode === "TaskActivityDate",
      );
      if (a) {
        answer = a;
        break;
      }
    }

    // Fallback: search for any answerValue string that contains computedDateTime
    if (!answer) {
      for (const fr of formResponses) {
        if (!fr.answers) continue;
        const a = fr.answers.find(
          (x: any) =>
            typeof x.answerValue === "string" &&
            x.answerValue.includes("computedDateTime"),
        );
        if (a) {
          answer = a;
          break;
        }
      }
    }
    let dueDate: string | undefined = undefined;
    if (
      answer &&
      answer.answerValue !== undefined &&
      answer.answerValue !== null
    ) {
      try {
        const parsed =
          typeof answer.answerValue === "string"
            ? JSON.parse(answer.answerValue)
            : answer.answerValue;
        dueDate =
          parsed?.computedDateTime ??
          parsed?.date ??
          parsed?.value ??
          String(answer.answerValue);
      } catch (_err) {
        dueDate = String(answer.answerValue);
      }
    }

    onHoldDueDate.value = dueDate;
  } catch (error) {
    showError("Error loading On Hold due date:", error);
    onHoldDueDate.value = undefined;
  }
};

// Watch for changes in activeOnHoldWorkflow and load the due date
watch(
  activeOnHoldWorkflow,
  async (newWorkflow) => {
    if (newWorkflow) {
      await loadOnHoldDueDate();
    } else {
      onHoldDueDate.value = undefined;
    }
  },
  { immediate: true },
);

const loadContactHistory = async () => {
  if (contactHistoryLoaded.value) return;
  if (!contactActivityDefinitionGuid.value && !onHoldDefinitionGuid.value)
    return;
  if (!myInfoStore.currentSysOrg?.guid) return;

  const appEntityGuid = localHousingApp.value?.entityGuid;
  if (!appEntityGuid) return;

  // Collect GUIDs of linked Contact Activity and On Hold workflows
  const linkedContactActivityGuids = new Set(
    linkedWorkflows.value
      .filter((w) => w.workflowDefinitionName === "Contact Activity")
      .map((w) => w.workflowGuid),
  );

  // Get linked On Hold workflow GUIDs to filter results
  const linkedOnHoldGuids = new Set(
    linkedWorkflows.value
      .filter((w) => w.workflowDefinitionName === "On Hold")
      .map((w) => w.workflowGuid),
  );

  if (linkedContactActivityGuids.size === 0 && linkedOnHoldGuids.size === 0) {
    contactHistoryLoaded.value = true;
    return;
  }

  const effectiveDefinitionGuids: string[] = [];
  if (contactActivityDefinitionGuid.value)
    effectiveDefinitionGuids.push(contactActivityDefinitionGuid.value);
  if (onHoldDefinitionGuid.value)
    effectiveDefinitionGuids.push(onHoldDefinitionGuid.value);

  contactHistoryLoading.value = true;
  try {
    const applicationNumber =
      localHousingApp.value?.applicationNumber?.toString();
    const housingApplicationSearch = applicationNumber
      ? `"${applicationNumber}"`
      : undefined;

    // Use contactActivity.search — same endpoint as the Contact Activities page —
    // scoped to this application so we get full ContactActivityDto shape.
    // Request the server max to reduce the chance of silently truncating older history.
    const result = await apiClient.contactActivity.search(
      myInfoStore.currentSysOrgGuid,
      effectiveDefinitionGuids,
      true, // includeArchived — show full history
      undefined, // entityGuid
      undefined, // contactNameSearch
      housingApplicationSearch, // housingApplicationSearch
      undefined, // createdByUserGuids
      undefined, // assignedToUserGuids
      undefined, // statusCodes
      undefined, // interactionType
      undefined, // direction
      undefined, // isImportant
      undefined, // dateFrom
      undefined, // dateTo
      undefined, // dateField
      undefined, // primaryEmailSearch
      undefined, // primaryPhoneSearch
      0, // skip
      1000, // take - request the server max for full history
      true, // ignoreFilters
    );

    // Filter to only rows that are actually linked to this housing application
    const allLinkedGuids = new Set([
      ...linkedContactActivityGuids,
      ...linkedOnHoldGuids,
    ]);

    contactHistoryActivities.value = (result.items ?? [])
      .filter((a) => allLinkedGuids.has(a.workflowGuid ?? a.guid))
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

    contactHistoryStatusList.value = result.statusList ?? [];
    contactHistoryLoaded.value = true;
  } catch (error) {
    showError(error);
  } finally {
    contactHistoryLoading.value = false;
  }
};

const onCollapseChange = (value: CollapseModelValue) => {
  const activeNames = Array.isArray(value) ? value : [value];
  if (activeNames.includes(ApplicationDrawerTabs.CONTACTHISTORY)) {
    loadContactHistory();
  }
};

// Watch for drawer opening and required data availability
watch(
  [
    () => props.drawerOpen,
    () => openedSections.value,
    () => contactActivityDefinitionGuid.value,
    () => onHoldDefinitionGuid.value,
    () => myInfoStore.currentSysOrg?.guid,
    () => localHousingApp.value?.entityGuid,
    () => drawerDataLoaded.value,
  ],
  ([
    isOpen,
    sections,
    contactActivityGuid,
    onHoldGuid,
    sysOrgGuid,
    appEntityGuid,
    dataLoaded,
  ]) => {
    if (
      isOpen &&
      sections.includes(ApplicationDrawerTabs.CONTACTHISTORY) &&
      (contactActivityGuid || onHoldGuid) &&
      sysOrgGuid &&
      appEntityGuid &&
      dataLoaded &&
      !contactHistoryLoaded.value
    ) {
      loadContactHistory();
    }
  },
);

const toggleEditPrimaryContact = async () => {
  if (editingContactSection.value) {
    if (contactInfoChanged.value) {
      try {
        await ElMessageBox.confirm(
          "You have unsaved contact instruction changes. Discard them?",
          "Unsaved Changes",
          {
            confirmButtonText: "Discard",
            cancelButtonText: "Keep Editing",
            type: "warning",
          },
        );
      } catch {
        return;
      }
    }
    editingContactSection.value = false;
  } else {
    contactInfoDraft.value = localHousingApp.value?.contactInfo ?? "";
    editingContactSection.value = true;
  }
};

const saveContactInfo = async () => {
  if (!localHousingApp.value) return;
  try {
    const dto = await apiClient.housingApplication.updateHousingApplication(
      localHousingApp.value.guid,
      { ...localHousingApp.value, contactInfo: contactInfoDraft.value || null },
    );
    localHousingApp.value = dto;
    ElMessage.success("Contact instructions saved");
  } catch (e) {
    showError(e);
  }
};

const handleSetPrimaryContact = async (person: LinkedPersonDto) => {
  if (!localHousingApp.value) return;
  try {
    await apiClient.housingApplication.setPrimaryContact(
      localHousingApp.value.guid,
      { personGuid: person.personGuid },
    );
    // Update local state
    linkedPeople.value = linkedPeople.value.map((p) => ({
      ...p,
      isPrimaryContact: p.personGuid === person.personGuid,
    }));
    ElMessage.success(`${person.name ?? "Contact"} set as primary contact`);
  } catch (e) {
    showError(e);
  }
};

const navigateToContact = (person: LinkedPersonDto) => {
  if (
    !person.entityGuid ||
    person.entityGuid === emptyGuid ||
    !person.entityDefinitionGuid ||
    person.entityDefinitionGuid === emptyGuid
  ) {
    ElMessage.error("This contact is missing CRM entity information.");
    return;
  }
  selectedContact.value = person;
  contactDrawerVisible.value = true;
};

const handleCreateContactActivity = async (person: LinkedPersonDto) => {
  if (!contactActivityDefinitionGuid.value) {
    showError("Contact Activity workflow definition not found.");
    return;
  }
  if (!localHousingApp.value?.entityGuid) {
    showError("Application has no linked entity.");
    return;
  }
  if (!myInfoStore.currentSysOrg?.guid) {
    showError("No current organization.");
    return;
  }

  try {
    // Create the Contact Activity workflow and link the housing application entity in one operation
    const newWorkflow =
      await apiClient.workflowInstance.createWorkflowWithLinks({
        workflowDefinitionName: "Contact Activity",
        orgGuid: myInfoStore.currentSysOrgGuid,
        primaryEntityGuid: person.entityGuid,
        linkedEntities: [
          {
            entityGuid: localHousingApp.value.entityGuid,
            linkType: "HousingApplication",
          },
        ],
        createContactActivity: true,
        housingApplicationEntityGuid: localHousingApp.value.entityGuid,
      });

    ElMessage.success("Contact Activity created");

    // Open the workflow drawer
    selectedWorkflow.value = newWorkflow;
  } catch (error) {
    showError(error);
  }
};

const loadLinkedWorkflows = async (entityGuid?: string) => {
  const guid = entityGuid || localHousingApp.value?.entityGuid;
  if (!guid) return;

  try {
    linkedWorkflows.value =
      await apiClient.workflowInstance.getLinkedWorkflowsWithArchived(
        guid,
        true,
      );
  } catch (error) {
    showError(error);
  }
};

/**
 * Load the full Housing Application workflow instance so ChangeStatusButton
 * components inside configured forms (PRS/Draft checklists) can update status.
 */
const loadHousingAppWorkflow = async () => {
  const summary = linkedWorkflows.value.find(
    (w) =>
      w.workflowDefinitionName === "Housing Application" ||
      w.workflowDefinitionName === "Legacy: Imported Information",
  );
  if (!summary) return;

  try {
    const content = await apiClient.workflowInstance.getWorkflow(
      summary.workflowGuid,
    );
    housingAppWorkflowInstance.value = content.workflow;
    housingAppWorkflowContent.value = content;

    joinWorkflowHub();
  } catch (error) {
    showError(error);
  }
};

// Provide workflow state so ChangeStatusButton works inside PRS/Draft checklists
const workflowState = useWorkflow({
  updateStatus: async (status: string) => {
    if (!housingAppWorkflowInstance.value?.guid) {
      showError("Housing Application workflow not found");
      return;
    }
    if (housingAppWorkflowInstance.value.status === status) return;

    housingAppWorkflowInstance.value.status = status;
    await apiClient.workflowInstance
      .updateWorkflow(
        housingAppWorkflowInstance.value.guid,
        housingAppWorkflowInstance.value,
      )
      .then(async (result) => {
        if (status === "ReadyForReview") {
          ElMessage.success("Successfully moved to New & Review");
          await loadHousingAppWorkflow();
          if (props.housingApp?.guid) {
            await handleRefreshHousingApp(props.housingApp.guid);
          }
          if (localHousingApp.value) {
            emit("updateHousingApp", localHousingApp.value);
          }
          emit("drawerClosed", localHousingApp.value);
        } else {
          ElMessage.success("Workflow status updated");
          await loadHousingAppWorkflow();
        }
        if (result.followUpWorkflowGuid) {
          await handleOpenFollowUpWorkflow(result.followUpWorkflowGuid);
        }
      })
      .catch(showError);
  },
  updateStatusFromNameStamp: async (status: string) => {
    if (!housingAppWorkflowInstance.value?.guid) {
      showError("Housing Application workflow not found");
      return;
    }
    if (housingAppWorkflowInstance.value.status === status) return;

    housingAppWorkflowInstance.value.status = status;
    await apiClient.workflowInstance
      .updateWorkflow(
        housingAppWorkflowInstance.value.guid,
        housingAppWorkflowInstance.value,
      )
      .then(async (result) => {
        if (status === "ReadyForReview") {
          ElMessage.success("Successfully moved to New & PRS");
          await loadHousingAppWorkflow();
          if (props.housingApp?.guid) {
            await handleRefreshHousingApp(props.housingApp.guid);
          }
          emit("drawerClosed", localHousingApp.value);
        } else {
          ElMessage.success("Workflow status updated");
          await loadHousingAppWorkflow();
        }
        if (result.followUpWorkflowGuid) {
          await handleOpenFollowUpWorkflow(result.followUpWorkflowGuid);
        }
      })
      .catch(showError);
  },
  getWorkflowStatus: () => housingAppWorkflowInstance.value?.status ?? "",
  getWorkflow: () => housingAppWorkflowInstance.value,
  deleteWorkflow: async () => {
    if (!housingAppWorkflowInstance.value?.guid) {
      showError("Housing Application workflow not found");
      return;
    }
    await apiClient.workflowInstance
      .deleteWorkflowInstance(housingAppWorkflowInstance.value.guid)
      .then(async () => {
        ElMessage.success("Workflow deleted");
        if (props.housingApp?.guid) {
          await handleRefreshHousingApp(props.housingApp.guid);
        }
        if (localHousingApp.value) {
          emit("updateHousingApp", localHousingApp.value);
        }
        emit("drawerClosed", localHousingApp.value);
      })
      .catch(showError);
  },
});
provide(workflowInjectKey, workflowState);

/**
 * Handle the event when a note is saved.
 * @param noteGuid The guid of the note that was saved
 */
const handleSaveNote = (noteGuid: string) => {
  // if the note was opened in the dialog, then it is a new note
  if (noteBeingEdited.value) {
    noteBeingEdited.value.status = FormResponseStatus.SUBMITTED;
    usersForms.value.push(noteBeingEdited.value);
    noteBeingEdited.value = undefined;
    showAddNoteDialog.value = false;
  }
  // otherwise, it was an abandoned note in the table that just got saved
  else {
    usersForms.value = usersForms.value.map((f) => {
      if (f.guid === noteGuid) {
        f.status = FormResponseStatus.SUBMITTED;
      }
      return f;
    });
  }
};

const getAnswersFromHousingApplication = async () => {
  const listOfQuestionCodes = [
    // seed data codes
    // prod data codes
    "AppPhone",
    "AppEmail",
    "CoAppAppPhone",
    "CoAppAppEmail",
  ];

  const housingApplicationForm = usersForms.value.find(
    (f) => f.formType === FormType.MAIN_APPLICATION,
  );

  if (
    housingApplicationForm?.guid &&
    housingApplicationForm?.formDefinitionGuid
  ) {
    await apiClient.formResponseAnswer
      .getAnswersFromCodes(
        housingApplicationForm?.guid,
        housingApplicationForm?.formDefinitionGuid,
        listOfQuestionCodes,
      )
      .then((answers) => {
        answersFromHousingApplication.value = answers;
      })
      .catch(showError);
  }
};

/**
 * Programmatically sync the read-only boolean answers on the Draft Checklist
 * form so the checkboxes reflect the real application state.
 *
 * "Locations Selected"          → true when preferred site results exist
 * "Application Form Completed"  → true when all required questions are answered
 */
const syncDraftChecklistAnswers = async () => {
  const draftChecklist = draftChecklistFormResponse.value;
  if (!draftChecklist) return;

  const orgGuid = myInfoStore.currentSysOrg?.guid;
  if (!orgGuid) return;

  try {
    // 1. Fetch / cache the draft checklist form definition (element GUIDs don't change)
    let draftFormResponse: FormResponse | undefined;
    if (!draftChecklistFormDef.value) {
      draftFormResponse = await apiClient.formResponse.getFormResponse(
        orgGuid,
        draftChecklist.formDefinitionGuid,
        draftChecklist.guid,
      );
      draftChecklistFormDef.value = draftFormResponse.formDefinition;
    }

    if (!draftChecklistFormDef.value) {
      console.warn(
        "Draft checklist form definition not available, skipping sync.",
      );
      return;
    }

    // 2. Find the boolean element GUIDs by question code
    const locationsElement = draftChecklistFormDef.value.components.find(
      (c) => c.questionCode === "DraftSelectLocations",
    );
    const appFormElement = draftChecklistFormDef.value.components.find(
      (c) => c.questionCode === "DraftApplicationForm",
    );

    if (!locationsElement || !appFormElement) {
      console.warn(
        "Draft checklist boolean elements not found, skipping sync.",
      );
      return;
    }

    // 3. Compute "Locations Selected"
    const locationsSelected = locationsSelectedOverride.value.length > 0;

    // 4. Compute "Application Form Completed" via unanswered question count
    //    in the Main Application form in the Housing Application workflow
    let appFormCompleted = false;
    const content = housingAppWorkflowContent.value;
    if (content) {
      const mainApplicationFormDefinition =
        content.workflow.workflowDefinition?.formDefinitions?.find(
          (fd) => fd.name === "Main Application",
        );

      const mainApplicationFormResponse = mainApplicationFormDefinition
        ? content.workflow.formResponses?.find(
            (fr) =>
              fr.formDefinitionGuid === mainApplicationFormDefinition.guid,
          )
        : undefined;

      let totalUnanswered = 0;
      if (mainApplicationFormDefinition && mainApplicationFormResponse) {
        totalUnanswered += getUnansweredQuestionCount(
          mainApplicationFormDefinition,
          mainApplicationFormResponse,
        );
      }
      appFormCompleted = totalUnanswered === 0;

      // if it is an imported information workflow then return true
      if (
        content.workflow.workflowDefinition?.name ===
        "Legacy: Imported Information"
      ) {
        appFormCompleted = true;
      }
    }

    // 5. Read the current answer values from the fetched response
    //    (re-fetch if we didn't already to get up-to-date answers)
    if (!draftFormResponse) {
      draftFormResponse = await apiClient.formResponse.getFormResponse(
        orgGuid,
        draftChecklist.formDefinitionGuid,
        draftChecklist.guid,
      );
    }

    const currentAnswers = draftFormResponse.answers ?? [];
    const currentLocationsAnswer = currentAnswers.find(
      (a) => a.formElementGuid === locationsElement.guid,
    );
    const currentAppFormAnswer = currentAnswers.find(
      (a) => a.formElementGuid === appFormElement.guid,
    );

    const currentLocationsValue = currentLocationsAnswer
      ? JSON.parse(currentLocationsAnswer.answerValue)
      : null;
    const currentAppFormValue = currentAppFormAnswer
      ? JSON.parse(currentAppFormAnswer.answerValue)
      : null;

    // 6. Submit only if the computed value differs from the stored answer
    let didUpdate = false;

    if (currentLocationsValue !== locationsSelected) {
      await apiClient.formResponseAnswer.submitAnswer(
        draftChecklist.guid,
        locationsElement.guid,
        emptyGuid,
        locationsElement.questionCode ?? "",
        draftChecklist.formDefinitionGuid,
        locationsSelected,
      );
      didUpdate = true;
    }

    if (currentAppFormValue !== appFormCompleted) {
      await apiClient.formResponseAnswer.submitAnswer(
        draftChecklist.guid,
        appFormElement.guid,
        emptyGuid,
        appFormElement.questionCode ?? "",
        draftChecklist.formDefinitionGuid,
        appFormCompleted,
      );
      didUpdate = true;
    }

    // 7. Bump the sync key so FormResponseTemplate re-fetches with updated answers
    if (didUpdate) {
      draftChecklistSyncKey.value++;
      // Clear cached response so FormResponseTemplate fetches fresh data
      draftChecklistFullResponse.value = undefined;
    } else {
      // No updates needed - cache the response to pass as prop
      draftChecklistFullResponse.value = draftFormResponse;
    }
  } catch (e) {
    showError("Failed to sync draft checklist answers:", e);
  }
};

const tableRowClassName = ({
  row,
}: {
  row: FormResponseDto;
  rowIndex: number;
}) => {
  // if row.updatedAt is  a few seconds ago, then highlight the row
  const timeDiff = new Date().getTime() - new Date(row.updatedAt).getTime();
  if (timeDiff < 4000) {
    return "new-row";
  }
  return "";
};

const handleRefreshHousingApp = async (housingApplicationGuid: string) => {
  if (!housingApplicationGuid) {
    showError("No application found. Cannot refresh.");
    return;
  }

  // Reset loading flags
  drawerDataLoaded.value = false;
  contactHistoryLoaded.value = false;

  // Fetch composite drawer data in parallel with cached stores
  const [drawerData] = await Promise.all([
    apiClient.housingApplication.getDrawerData(housingApplicationGuid),
    loginAccountsStore.load(),
    housingSiteStore.load(),
    orgInfoStore.load(),
  ]);

  // Distribute composite response to individual refs
  if (!_.isEqual(drawerData.housingApplication, localHousingApp.value)) {
    localHousingApp.value = drawerData.housingApplication;
    if (!newOccupancyDate.value && localHousingApp.value?.occupancyDate) {
      newOccupancyDate.value = localHousingApp.value.occupancyDate;
    }
  }
  overrideHousingFinderInputs.value = drawerData.finderInput;
  usersForms.value = drawerData.formResponses ?? [];
  linkedPeople.value = drawerData.linkedPeople ?? [];
  linkedWorkflows.value = drawerData.linkedWorkflows ?? [];
  intakeScenarioEntries.value = drawerData.intakeScenarioEntries ?? [];

  // Reset timeline so it lazy-loads when History section is expanded
  timeline.value = [];

  // Load full workflow instance for ChangeStatusButton support in configured forms
  await loadHousingAppWorkflow();

  drawerDataLoaded.value = true;

  // this needs the application form to be fetched first so it can't go in the `Promise.all`
  await getAnswersFromHousingApplication();

  // Sync draft checklist checkboxes to reflect actual application state
  await syncDraftChecklistAnswers();

  if (timelineLoaded.value && localHousingApp.value) {
    // Refresh timeline if it's already loaded, to reflect any changes
    await getApplicationTimeLine(localHousingApp.value.guid);
  }
};

const handleClickRefreshApplication = async () => {
  if (!localHousingApp.value?.guid) {
    showError(
      "Developer Error: clicked refresh housing application with no guid",
    );
    return;
  }

  await handleRefreshHousingApp(localHousingApp.value.guid);
};

const startPresenceForApp = async (guid: string) => {
  if (currentPresenceGuid === guid) return;
  if (currentPresenceGuid) {
    await stopPresenceConnection();
    isCurrentUserIdle.value = false;
    controlRequestPending.value = false;
  }
  try {
    await startPresenceConnection(guid);
    currentPresenceGuid = guid;
    resetIdleTimer();

    onControlRequestReceived(async (request: any) => {
      try {
        await ElMessageBox.confirm(
          `${request.requestingUserName} is requesting edit control. Grant access?`,
          "Control Request",
          {
            confirmButtonText: "Grant",
            cancelButtonText: "Deny",
            type: "warning",
          },
        );
        await respondToControlRequest(
          guid,
          request.requestingConnectionId,
          true,
        );
      } catch {
        await respondToControlRequest(
          guid,
          request.requestingConnectionId,
          false,
        );
      }
    });

    onControlRequestResponse((response: any) => {
      controlRequestPending.value = false;
      if (response.approved) {
        ElMessage.success("Control granted! You can now edit.");
      } else {
        ElMessage.info(`${response.fromUserName} denied your control request.`);
      }
    });
  } catch {
    // SignalR connection is best-effort; don't block the drawer
  }
};

watch(
  () => props.housingApp,
  async (newValue, oldValue) => {
    if (!newValue) {
      showError("No application found.");
      return;
    }
    const guidToUse = newValue.guid ?? localHousingApp.value?.guid;
    if (!guidToUse) {
      showError("No application GUID available to refresh.");
      return;
    }

    // Leave old presence group if switching applications
    if (oldValue && oldValue.guid !== newValue.guid && currentPresenceGuid) {
      await stopPresenceConnection();
      currentPresenceGuid = null;
      isCurrentUserIdle.value = false;
      controlRequestPending.value = false;
    }

    await handleRefreshHousingApp(guidToUse);
    await startPresenceForApp(guidToUse);
  },
  { immediate: true },
);

const convertToWorkflowDto = (
  linkedWorkflow: LinkedWorkflowSummaryDto,
): WorkflowDto => {
  return {
    guid: linkedWorkflow.workflowGuid,
    workflowStatus: linkedWorkflow.workflowStatus ?? "",
    workflowName: linkedWorkflow.workflowName ?? "",
    workflowArchivedAt: linkedWorkflow.workflowArchivedAt ?? "",
    workflowDefinitionName: linkedWorkflow.workflowDefinitionName ?? "",
    workflowDefinitionGuid: linkedWorkflow.workflowDefinitionGuid ?? "",
    ownerName: linkedWorkflow.primaryEntityName ?? "",
    ownerGuid: linkedWorkflow.primaryEntityGuid,
    answersToShow: {},
    updatedAt: linkedWorkflow.linkedAt ?? "",
  };
};

const openApplicationWorkflow = async () => {
  if (linkedWorkflows.value.length === 0) {
    ElMessage.warning("No workflows found for this application.");
    return;
  }
  const applicationWorkflow = linkedWorkflows.value.find(
    (w) =>
      w.workflowDefinitionName === "Housing Application" ||
      w.workflowDefinitionName === "Legacy: Imported Information",
  );
  if (!applicationWorkflow) {
    ElMessage.warning("No Housing Application workflow found.");
    return;
  }
  ElMessage.info("Opening...");
  await openWorkflow(applicationWorkflow.workflowGuid);
};

const openWorkflow = async (workflowGuid: string) => {
  const linkedWf = linkedWorkflows.value.find(
    (w) => w.workflowGuid === workflowGuid,
  );
  if (linkedWf) {
    selectedWorkflow.value = convertToWorkflowDto(linkedWf);
  } else {
    try {
      const content =
        await apiClient.workflowInstance.getWorkflow(workflowGuid);
      selectedWorkflow.value = {
        guid: content.workflow.guid,
        workflowStatus: content.workflow.status ?? "",
        workflowName: content.workflow.name ?? "",
        workflowArchivedAt: content.workflow.archivedAt ?? "",
        workflowDefinitionName: content.workflow.workflowDefinition?.name ?? "",
        workflowDefinitionGuid: content.workflow.workflowDefinition?.guid ?? "",
        ownerName: localHousingApp.value?.applicantName ?? "",
        ownerGuid: localHousingApp.value?.entityGuid,
        answersToShow: {},
        updatedAt: content.workflow.createdAt ?? "",
      };
    } catch (error) {
      showError(error);
    }
  }
};

const handleLinkedWorkflowSelected = async (
  workflowGuid: string,
  formResponseGuid?: string,
) => {
  if (formResponseGuid) {
    selectedWorkflowFocusFormResponseGuid.value = formResponseGuid;
  }
  await openWorkflow(workflowGuid);
};

const handleWorkflowDrawerClose = async () => {
  // Invalidate contact history cache if a Contact Activity or On Hold workflow
  // was viewed/edited/deleted, since both types appear in the contact history table.
  const shouldRefreshContactHistory =
    selectedWorkflow.value?.workflowDefinitionName === "Contact Activity" ||
    selectedWorkflow.value?.workflowDefinitionName === "On Hold";

  if (shouldRefreshContactHistory) {
    contactHistoryLoaded.value = false;
  }
  selectedWorkflow.value = null;
  selectedWorkflowFocusFormResponseGuid.value = null;
  await handleRefreshHousingApp(
    (props.housingApp?.guid ?? localHousingApp.value?.guid)!,
  );
  if (shouldRefreshContactHistory) {
    await loadContactHistory();
  }
};

const handleWorkflowUpdated = async () => {
  // Invalidate contact history so the table reflects any deleted/updated workflows
  contactHistoryLoaded.value = false;
  await handleRefreshHousingApp(
    (props.housingApp?.guid ?? localHousingApp.value?.guid)!,
  );

  // Clear the inline intake workflow if it was deleted
  if (
    intakeWorkflowLocal.value &&
    !linkedWorkflows.value.some(
      (w) => w.workflowGuid === intakeWorkflowLocal.value!.guid,
    )
  ) {
    intakeWorkflowLocal.value = null;
  }

  await loadContactHistory();
};

const handleOpenFollowUpWorkflow = async (workflowGuid: string) => {
  selectedWorkflow.value = null;
  await loadLinkedWorkflows();
  await nextTick();
  await openWorkflow(workflowGuid);

  // Confirm to the user that they are now viewing the new follow-up workflow
  const linkedWf = linkedWorkflows.value.find(
    (w) => w.workflowGuid === workflowGuid,
  );
  const name = linkedWf?.workflowName;
  if (name) {
    const msg = `Now viewing: "${name}"`;
    ElMessage.success({
      message: msg,
      duration: getMessageTime(msg),
      showClose: true,
    });
  }
};

// Stable handler references for the workflowInstance hub (shared connection).
// We use specific handler refs so we only remove *our* listeners.
const workflowMsgHandler = (obj: any) => {
  console.log("Message received:", obj);
  if (obj.success) {
    ElMessage.info({
      message: obj.msg,
      duration: getMessageTime(obj.msg),
      showClose: true,
    });
  } else {
    showError(obj.msg);
  }
};

const workflowStatusChangedHandler = async (obj: any) => {
  console.log("Workflow status changed:", obj);
  // do we want to update the status, or is it being updated already?
};

const joinWorkflowHub = async () => {
  // join hub to listen for workflow update messages
  const workflowGuid = housingAppWorkflowContent.value?.workflow.guid;
  if (!workflowGuid) {
    console.log(
      "No workflow GUID available, skipping workflow hub connection.",
    );
    return;
  }
  console.log("Joining workflow hub for workflow GUID:", workflowGuid);

  workFlowHub = await hubManager.getHub(
    "workflowInstance",
    "/workflowInstance",
  );
  if (workFlowHub) {
    // Use specific handler refs so we only remove listeners added by *this* component.
    workFlowHub.off("Msg", workflowMsgHandler);
    workFlowHub.off("StatusChanged", workflowStatusChangedHandler);

    workFlowHub.on("Msg", workflowMsgHandler);
    workFlowHub.on("StatusChanged", workflowStatusChangedHandler);

    try {
      await workFlowHub.invoke(
        "JoinWorkflowGroup",
        workflowGuid,
        myInfoStore.userInfo.loginName,
      );
    } catch (error) {
      showError("Error joining workflow group:", error);
    }
  }
};

// Safe leave for the workflow group on the shared "workflowInstance" hub.
// We only remove our specific handlers (never bare .off) and never stop the connection.
const leaveHub = async () => {
  const guid = housingAppWorkflowContent.value?.workflow.guid;
  if (guid && workFlowHub) {
    try {
      await workFlowHub.invoke("LeaveWorkflowGroup", guid);
    } catch (error) {
      showError("Error leaving workflow group", error);
    }
  }

  if (workFlowHub) {
    workFlowHub.off("Msg", workflowMsgHandler);
    workFlowHub.off("StatusChanged", workflowStatusChangedHandler);
  }

  // Do not stop the shared connection (hubManager caches by name "workflowInstance").
  // Other components (e.g. WorkflowResponseViewer) may still be using it.
};

let workFlowHub: HubConnection | null = null;

onMounted(async () => {
  await Promise.all([
    loginAccountsStore.load(),
    housingSiteStore.load(),
    myInfoStore.load(),
    orgInfoStore.load(),
    myHousingOrgRolesStore.load(),
    loadUnitTypeTree(),
    loadSectorTree(),
    loadBuildingTrees(),
  ]);
  // In dev, onMounted can fire more than once for the same instance without unmount.
  // leaveHub safely removes only our workflow handlers + leaves the server group.
  await leaveHub();
});
</script>
<template>
  <el-drawer
    v-if="localHousingApp"
    :model-value="drawerOpen"
    direction="rtl"
    size="80%"
    class="housing-application-drawer"
    :show-close="false"
    @closed="handleClose"
  >
    <template #header="{ titleId, titleClass }">
      <h3 :id="titleId" :class="titleClass">
        {{ localHousingApp.applicantName }}
        <EnumTag
          :model-value="localHousingApp.status"
          size="large"
          type="housingApplicationStatus"
        />
      </h3>
      <RefreshButton @click="handleClickRefreshApplication" />
      <el-button type="primary" @click="handleClose"> Done </el-button>
    </template>
    <div
      class="drawer__content"
      @mousedown.capture="handleDrawerActivity"
      @keydown.capture="handleDrawerActivity"
    >
      <div v-if="currentEditorName" class="app-editor-banner">
        <el-alert type="warning" center :closable="false">
          <template #default>
            Currently being edited by {{ currentEditorName }}
            <el-button
              :loading="controlRequestPending"
              type="warning"
              size="small"
              @click="
                controlRequestPending = true;
                requestApplicationControl(localHousingApp!.guid);
              "
            >
              Request Control
            </el-button>
          </template>
        </el-alert>
      </div>
      <div
        v-else-if="presenceViewers.length > 1 && !effectiveReadOnly"
        class="app-viewers-bar"
      >
        <el-alert type="info" center :closable="false">
          <span
            >Currently being viewed by:
            {{ presenceOtherViewers.join(", ") }}</span
          >
        </el-alert>
      </div>
      <div v-if="isCurrentUserIdle" class="app-idle-banner">
        <el-alert type="warning" :closable="false" center>
          <template v-if="canResumeEditing">
            <span>You've been idle and switched to read-only.</span>
            <el-button type="primary" size="small" @click="resumeEditing">
              Resume Editing
            </el-button>
          </template>
          <span v-else>
            You've been idle.
            {{ idleEditorName ?? "Another user" }} is now editing this
            application.
          </span>
        </el-alert>
      </div>
      <div class="collapse">
        <el-collapse v-model="openedSections" @change="onCollapseChange">
          <el-collapse-item
            title="Summary"
            :name="ApplicationDrawerTabs.APPSUM"
          >
            <div class="application-summary">
              <!-- LEFT COLUMN: Application info -->
              <div class="summary-info">
                <div class="summary-info-row">
                  <div>
                    <span class="sub-header">PRS Score: </span
                    >{{
                      localHousingApp.needsAssessmentScore === null ||
                      localHousingApp.needsAssessmentScore === undefined
                        ? "-"
                        : localHousingApp.needsAssessmentScore
                    }}
                  </div>
                  <div>
                    <template v-if="localHousingApp.dateWaitlisted">
                      <span class="sub-header">Days Waiting: </span>
                      {{ daysWaiting }}
                    </template>
                    <template v-else-if="localHousingApp.dateSubmitted">
                      <span class="sub-header">Days in Review: </span>
                      {{ daysInReview }}
                    </template>
                  </div>
                </div>
                <div class="summary-info-row">
                  <div>
                    <span class="sub-header">Earliest Occupancy: </span>
                    <el-date-picker
                      :model-value="
                        localHousingApp.earliestOccupancyDate ?? undefined
                      "
                      :disabled="effectiveReadOnly"
                      style="max-width: 120px"
                      size="small"
                      @update:model-value="updateEarliestOccupancyDate"
                    />
                  </div>
                </div>
                <div class="summary-info-row">
                  <div>
                    <span class="sub-header">Created By: </span
                    >{{ createdByUser }}
                  </div>
                  <div>
                    <span class="sub-header">Source: </span>
                    {{ formatSource(localHousingApp.source || "-") }}
                  </div>
                </div>
                <div
                  v-for="section in answersFromHousingApplication"
                  :key="section.sectionName"
                  class="summary-info-row"
                >
                  <div v-for="val in section.labelAndAnswer" :key="val.label">
                    <span class="sub-header">{{ val.label }}: </span>
                    <span>{{ val.answer }}</span>
                  </div>
                </div>
                <div class="summary-info-row assign-to">
                  <span class="sub-header">Assigned To:</span>
                  <AssignTo
                    v-model:housing-application="localHousingApp"
                    :disabled="effectiveReadOnly"
                  />
                </div>
              </div>

              <!-- RIGHT COLUMN: Contacts box -->
              <div class="summary-contacts-box">
                <!-- Contacts table -->
                <el-table
                  v-if="sortedLinkedPeople.length > 0"
                  :data="sortedLinkedPeople"
                  size="small"
                  :row-class-name="
                    ({ row }) =>
                      row.isPrimaryContact ? 'primary-contact-row' : ''
                  "
                  stripe
                >
                  <el-table-column label="" width="35" align="center">
                    <template #default="{ row }">
                      <el-button
                        v-if="editingContactSection"
                        class="set-primary-btn"
                        size="small"
                        :type="row.isPrimaryContact ? 'warning' : 'default'"
                        :plain="!row.isPrimaryContact"
                        aria-label="Set as primary contact"
                        @click.stop="handleSetPrimaryContact(row)"
                      >
                        <el-icon aria-hidden="true"><StarFilled /></el-icon>
                      </el-button>
                      <el-icon
                        v-else-if="row.isPrimaryContact"
                        class="primary-star"
                        role="img"
                        aria-label="Primary contact"
                        ><StarFilled
                      /></el-icon>
                    </template>
                  </el-table-column>

                  <el-table-column label="Role" width="130">
                    <template #default="{ row }">
                      {{ row.contactType || row.entityDefinitionName || "-" }}
                    </template>
                  </el-table-column>

                  <el-table-column label="Name" min-width="140" width="190">
                    <template #default="{ row }">
                      <template
                        v-if="row.entityDefinitionGuid && row.entityGuid"
                      >
                        <strong v-if="row.isPrimaryContact">
                          <el-link @click="navigateToContact(row)">{{
                            row.name || "-"
                          }}</el-link>
                        </strong>
                        <el-link v-else @click="navigateToContact(row)">{{
                          row.name || "-"
                        }}</el-link>
                      </template>
                      <template v-else>
                        <strong v-if="row.isPrimaryContact">{{
                          row.name || "-"
                        }}</strong>
                        <span v-else>{{ row.name || "-" }}</span>
                      </template>
                    </template>
                  </el-table-column>

                  <el-table-column label="Phone" width="130">
                    <template #default="{ row }">
                      {{ row.primaryPhone || "-" }}
                    </template>
                  </el-table-column>

                  <el-table-column label="Email" min-width="160" width="200">
                    <template #default="{ row }">
                      {{ row.primaryEmail || "-" }}
                    </template>
                  </el-table-column>

                  <el-table-column label="" width="120" align="right">
                    <template #default="{ row }">
                      <el-button
                        size="small"
                        type="primary"
                        plain
                        :disabled="!contactActivityDefinitionGuid"
                        @click.stop="handleCreateContactActivity(row)"
                      >
                        + Activity
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>

                <!-- Fallback when no linked people -->
                <div v-else class="no-contacts-fallback">
                  <div>
                    <span class="sub-header">Applicant: </span>
                    {{ localHousingApp.applicantName }}
                  </div>
                  <div v-if="localHousingApp.coApplicantName">
                    <span class="sub-header">Co-applicant: </span>
                    {{ localHousingApp.coApplicantName || "-" }}
                  </div>
                </div>

                <!-- Application-level Contact Instructions -->
                <div
                  :class="[
                    'contact-instructions',
                    { 'has-content': localHousingApp?.contactInfo },
                  ]"
                >
                  <div class="contact-instructions-inner">
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      "
                    >
                      <strong>Contact Instructions:</strong>
                      <el-button
                        size="small"
                        :type="editingContactSection ? 'primary' : 'default'"
                        plain
                        :disabled="effectiveReadOnly"
                        title="Edit primary contact and contact instructions"
                        @click="toggleEditPrimaryContact"
                      >
                        <template v-if="editingContactSection">Done</template>
                        <template v-else>Edit</template>
                      </el-button>
                    </div>
                    <template v-if="editingContactSection">
                      <div class="contact-info-edit">
                        <el-input
                          v-model="contactInfoDraft"
                          type="textarea"
                          :autosize="{ minRows: 1, maxRows: 4 }"
                          maxlength="250"
                          show-word-limit
                          placeholder="Enter contact instructions..."
                          style="flex: 1"
                        />
                        <el-button
                          size="small"
                          type="primary"
                          :disabled="!contactInfoChanged"
                          @click="saveContactInfo"
                          >Save</el-button
                        >
                      </div>
                    </template>
                    <template v-else>
                      <span
                        :style="
                          !localHousingApp?.contactInfo ? 'color: #b0b0b0' : ''
                        "
                      >
                        {{ localHousingApp?.contactInfo || "None" }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action buttons below the grid -->
            <div class="summary-buttons">
              <div class="summary-buttons-row">
                <el-button
                  v-if="canMoveBackToReview"
                  :disabled="effectiveReadOnly"
                  @click="handleMoveBackToReview"
                  >Move Back to Review</el-button
                >
                <el-button
                  v-if="
                    !activeOnHoldWorkflow &&
                    localHousingApp.status !==
                      HousingApplicationStatus.CANCELLED &&
                    !localHousingApp.dateArchived &&
                    localHousingApp.status !==
                      HousingApplicationStatus.LOCKED_INTERVIEWING &&
                    localHousingApp.status !==
                      HousingApplicationStatus.LOCKED_WITH_OFFER &&
                    localHousingApp.status !==
                      HousingApplicationStatus.OFFER_ACCEPTED
                  "
                  :disabled="
                    effectiveReadOnly ||
                    localHousingApp.status === HousingApplicationStatus.ON_HOLD
                  "
                  type="info"
                  plain
                  @click="handleCancelApplication"
                  >Cancel Application</el-button
                >
                <!-- <el-button @click="handleRequestInformation"
                  >Request Information</el-button
                > -->
                <el-button
                  v-if="
                    !activeOnHoldWorkflow &&
                    localHousingApp.status !==
                      HousingApplicationStatus.ON_HOLD &&
                    localHousingApp.status !==
                      HousingApplicationStatus.CANCELLED
                  "
                  :disabled="effectiveReadOnly"
                  @click="handlePlaceOnHold"
                  >Place on Hold</el-button
                >
                <el-button
                  v-if="activeOnHoldWorkflow"
                  :disabled="effectiveReadOnly"
                  @click="handleEditOrRemoveOnHold"
                >
                  Edit Or Remove On Hold
                </el-button>
                <span
                  v-if="formattedOnHoldDueDate"
                  class="on-hold-due"
                  title="On hold due date"
                >
                  <el-icon class="on-hold-due__icon"><Calendar /></el-icon>
                  <span class="on-hold-due__date">
                    {{ formattedOnHoldDueDate }}
                  </span>
                  <span
                    v-if="relativeOnHoldDueDate"
                    class="on-hold-due__relative"
                  >
                    &nbsp;·&nbsp;{{ relativeOnHoldDueDate }}
                  </span>
                </span>
              </div>
              <el-alert
                v-if="
                  localHousingApp.status ===
                  HousingApplicationStatus.LOCKED_INTERVIEWING
                "
                type="warning"
                show-icon
                :closable="false"
                title="This applicant is currently in an active intake process. Placing on hold or cancelling will cease intake."
                style="margin-top: 8px"
              />
            </div>
          </el-collapse-item>

          <!-- ── Contact History ── -->
          <el-collapse-item
            title="Contact History"
            :name="ApplicationDrawerTabs.CONTACTHISTORY"
          >
            <ContactActivityTable
              v-if="
                contactHistoryActivities.length > 0 || contactHistoryLoading
              "
              :data="contactHistoryActivities"
              :status-list="contactHistoryStatusList"
              :activity-type-options="contactHistoryActivityTypeOptions"
              :loading="contactHistoryLoading"
              :show-housing-application-column="false"
              @activity-click="
                (activity: ContactActivityDto) =>
                  openWorkflow(activity.workflowGuid ?? activity.guid)
              "
            />
            <el-empty
              v-else-if="!contactHistoryLoading && contactHistoryLoaded"
              description="No contact activities found"
            />
          </el-collapse-item>

          <el-collapse-item
            title="Application Details"
            :name="ApplicationDrawerTabs.FORMS"
          >
            <div class="application-details">
              <div v-if="notes.length > 0" class="notes-section">
                <span class="sub-header"
                  >Legacy Notes ({{ notes.length }})</span
                >
                <el-button
                  size="small"
                  @click="notesExpanded = !notesExpanded"
                  >{{ notesExpanded ? "Hide" : "Show" }}</el-button
                >

                <el-table
                  v-show="notesExpanded"
                  :data="notes"
                  :default-sort="{ prop: 'updatedAt', order: 'descending' }"
                  style="width: 100%"
                  max-height="250"
                  size="small"
                  :row-class-name="tableRowClassName"
                >
                  <el-table-column label="Note" width="700">
                    <template #default="scope">
                      <ApplicationNote
                        :key="scope.row.guid"
                        class="note"
                        :form-guid="scope.row.formDefinitionGuid"
                        :response-guid="scope.row.guid"
                        @note-saved="($event: string) => handleSaveNote($event)"
                      />
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="createdByUser"
                    label="Created by"
                    width="160"
                  >
                    <template #default="scope">
                      {{
                        loginAccountsStore.List.find(
                          (acc) => acc.userGuid === scope.row.createdByUser,
                        )?.loginName
                      }}
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="createdAt"
                    label="Date Created"
                    width="150"
                    sortable
                  >
                    <template #default="scope">
                      <DateWithTimePopover :date="scope.row.createdAt" />
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <div class="application-details-card">
                <div>
                  <b style="margin-right: 8px"
                    >Selected Locations ({{ locationsSelectedOverride.length
                    }}{{ locationsSelectedHasOverride ? "*" : "" }})</b
                  >

                  <el-button
                    size="small"
                    @click="
                      selectedLocationsExpanded = !selectedLocationsExpanded
                    "
                    >{{
                      selectedLocationsExpanded ? "Hide" : "Show"
                    }}</el-button
                  >

                  <el-button
                    size="small"
                    type="primary"
                    @click="() => handleOpenFinder(true)"
                    >View In Finder
                  </el-button>

                  <el-button
                    size="small"
                    type="primary"
                    :disabled="effectiveReadOnly"
                    @click="() => handleOpenFinder(false)"
                    >Edit in Finder
                  </el-button>
                </div>

                <div v-show="selectedLocationsExpanded">
                  <template v-if="locationsSelectedHasOverride">
                    <el-tabs type="border-card" style="margin-top: 8px">
                      <el-tab-pane label="HMB Override">
                        <ApplicationLocations
                          v-if="housingApp?.guid"
                          :application-guid="housingApp.guid"
                          :finder-inputs="overrideHousingFinderInputs"
                          :locations-selected="locationsSelectedOverride"
                          class="finder-info"
                        />
                      </el-tab-pane>
                      <el-tab-pane label="Original">
                        <ApplicationLocations
                          v-if="housingApp?.guid"
                          :application-guid="housingApp.guid"
                          :finder-inputs="localHousingApp.finderInput"
                          :locations-selected="locationsSelectedOriginal"
                          class="finder-info"
                        />
                      </el-tab-pane>
                    </el-tabs>
                  </template>
                  <template v-else>
                    <ApplicationLocations
                      v-if="housingApp?.guid"
                      :application-guid="housingApp.guid"
                      :finder-inputs="overrideHousingFinderInputs"
                      :locations-selected="locationsSelectedOriginal"
                      class="finder-info"
                    />
                  </template>
                </div>
              </div>
              <div
                v-if="localHousingApp.entityGuid"
                class="application-details-card"
              >
                <LinkedWorkflowsTable
                  :key="nonInteractionWorkflows.length"
                  :workflows="nonInteractionWorkflows"
                  @workflow-selected="handleLinkedWorkflowSelected"
                />
              </div>
            </div>
          </el-collapse-item>
          <el-collapse-item
            v-if="localHousingApp.status === HousingApplicationStatus.DRAFT"
            title="Draft"
            :name="ApplicationDrawerTabs.OFFLINEAPPS"
            :class="{
              'highlighted-section': originalSectionsOpened.includes(
                ApplicationDrawerTabs.OFFLINEAPPS,
              ),
            }"
          >
            <FormResponseTemplate
              v-if="draftChecklistFormResponse"
              :key="
                'draft-form-' +
                localHousingApp.dateLastActivity +
                '-' +
                draftChecklistSyncKey
              "
              :form-guid="draftChecklistFormResponse.formDefinitionGuid"
              :response-guid="draftChecklistFormResponse.guid"
              :response="draftChecklistFullResponse"
              :incoming-display-mode="
                effectiveReadOnly ||
                localHousingApp.status !== HousingApplicationStatus.DRAFT
                  ? FormDisplayMode.READ_ONLY
                  : FormDisplayMode.EDIT
              "
            />
            <HousingApplicationOfflineSection
              v-else
              :key="'draft-offline-' + localHousingApp.dateLastActivity"
              :housing-application="localHousingApp"
              @open:finder="() => handleOpenFinder(false)"
              @open:application-form="() => openApplicationWorkflow()"
            />
          </el-collapse-item>
          <el-collapse-item
            v-if="
              localHousingApp.status === HousingApplicationStatus.NEW ||
              localHousingApp.status === HousingApplicationStatus.IN_REVIEW
            "
            title="New & Review"
            :name="ApplicationDrawerTabs.NEWANDPRS"
            :class="{
              'highlighted-section': originalSectionsOpened.includes(
                ApplicationDrawerTabs.NEWANDPRS,
              ),
            }"
          >
            <!-- text to show if the application is in any status <= Waitlisted -->
            <!-- <div
              v-if="localHousingApp.status === HousingApplicationStatus.DRAFT"
              class="info-msg"
            >
              This application is still in draft mode. It must be submitted
              before any actions can be performed.
            </div>

            <div
              v-else-if="
                localHousingApp.status === HousingApplicationStatus.WAITLIST
              "
              class="info-msg"
            >
              This application is already on the waitlist.
            </div> -->

            <div>
              <FormResponseTemplate
                v-if="prsChecklistFormResponse"
                :key="'prs-form-' + localHousingApp.dateLastActivity"
                :form-guid="prsChecklistFormResponse.formDefinitionGuid"
                :response-guid="prsChecklistFormResponse.guid"
                :workflow="housingAppWorkflowInstance"
                :incoming-display-mode="
                  effectiveReadOnly
                    ? FormDisplayMode.READ_ONLY
                    : FormDisplayMode.EDIT
                "
              />
              <HousingApplicationOfflineSection
                v-else
                :key="'prs-offline-' + localHousingApp.dateLastActivity"
                :housing-application="localHousingApp"
                @open:finder="() => handleOpenFinder(false)"
                @open:application-form="() => openApplicationWorkflow()"
              />
            </div>
          </el-collapse-item>
          <el-collapse-item
            v-if="
              statusAtLeast(
                localHousingApp.status,
                HousingApplicationStatus.IN_REVIEW,
              )
            "
            title="Vacancy Management"
            :name="ApplicationDrawerTabs.INTAKEANDOFFER"
            :class="{
              'highlighted-section': originalSectionsOpened.includes(
                ApplicationDrawerTabs.INTAKEANDOFFER,
              ),
            }"
          >
            <div v-if="waitlistScenario" class="intake-offer-section">
              <div
                v-if="
                  lockedInScenarioEntry &&
                  reviewFitScenario?.name !==
                    lockedInScenarioEntry.scenarioName &&
                  waitlistScenario?.name !== lockedInScenarioEntry.scenarioName
                "
                :key="lockedInScenarioEntry.scenarioGuid"
                class="vacancy-mgmt-card 'intake-entry--active'"
              >
                <div class="vacancy-mgmt-header">
                  <span class="vacancy-mgmt-title">{{
                    lockedInScenarioEntry.scenarioName ?? "Scenario"
                  }}</span>
                  <span
                    v-if="lockedInScenarioEntry.siteName"
                    class="vacancy-mgmt-site"
                    >{{ lockedInScenarioEntry.siteName }}</span
                  >
                  <span
                    v-if="lockedInScenarioEntry.unitType"
                    class="vacancy-mgmt-site"
                    >{{
                      intakeOfferFormatUnitType(lockedInScenarioEntry.unitType)
                    }}</span
                  >
                  <EnumTag
                    v-if="lockedInScenarioEntry.status"
                    :model-value="lockedInScenarioEntry.status"
                    size="small"
                    type="housingIntakeScenarioApplicationStatus"
                  />
                  <el-tag v-else size="small" type="info">Waitlist</el-tag>
                </div>
              </div>

              <!-- ─── Scenario + History combined ─── -->
              <div v-if="intakeDisplayScenario" class="vacancy-mgmt-card">
                <div class="vacancy-mgmt-header">
                  <span class="vacancy-mgmt-title">{{
                    intakeDisplayScenario.name ??
                    formatIntakeScenarioName(intakeDisplayScenario) ??
                    "Scenario"
                  }}</span>
                  <span class="vacancy-mgmt-site">{{
                    intakeDisplayScenario?.housingSite?.name ?? ""
                  }}</span>
                  <EnumTag
                    v-if="currentProcessedStatus"
                    :model-value="currentProcessedStatus"
                    size="small"
                    type="housingIntakeScenarioApplicationStatus"
                  />
                  <el-tag v-else size="small" type="info">Waitlist</el-tag>
                </div>

                <!-- Vacancy Months Row -->
                <div
                  v-if="intakeDisplayScenario?.vacancies?.length"
                  class="vacancy-months-row"
                >
                  <div class="vacancy-months-boxes">
                    <div
                      v-for="m in intakeVacancyByMonth"
                      :key="m.key"
                      class="month-box"
                    >
                      <span class="month-value">{{ m.available }}</span>
                      <span class="month-label">{{ m.label }}</span>
                    </div>
                    <div class="month-box total-box">
                      <span class="month-value"
                        >{{ intakeTotalFilled }} / {{ intakeTotalUnits }}</span
                      >
                      <span class="month-label">Total Units</span>
                    </div>
                    <el-button
                      v-if="matchedVacancies && matchedVacancies.length > 0"
                      type="primary"
                      text
                      size="small"
                      class="details-toggle-btn"
                      @click="
                        matchedVacanciesVisible = !matchedVacanciesVisible
                      "
                    >
                      {{
                        matchedVacanciesVisible
                          ? "Hide Matched"
                          : "View Matched"
                      }}
                      ({{ matchedVacancies.length }})
                    </el-button>
                  </div>
                </div>

                <el-table
                  v-if="matchedVacanciesVisible && matchedVacancies?.length"
                  :data="matchedVacancies"
                  size="small"
                  stripe
                  class="vacancy-mgmt-table"
                  :show-header="true"
                >
                  <el-table-column label="Available" width="100">
                    <template #default="{ row }">
                      {{ formatDate(row.dateAvailable) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Units" width="70" align="center">
                    <template #default="{ row }">
                      {{
                        row.unitNumbers?.length
                          ? row.unitNumbers.join(", ")
                          : "—"
                      }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Open" width="60" align="center">
                    <template #default="{ row }">
                      <strong>{{
                        row.totalUnits - (row.unitsFilled ?? 0)
                      }}</strong>
                    </template>
                  </el-table-column>
                </el-table>

                <!-- Scenario vs Application comparison grid -->
                <div class="vacancy-mgmt-compare">
                  <div class="compare-grid">
                    <!-- Header row -->
                    <div class="compare-label"></div>
                    <div class="compare-col-header compare-col--scenario">
                      Scenario
                    </div>
                    <div class="compare-col-header compare-col--app">
                      Applicant
                      <el-button
                        text
                        size="small"
                        type="primary"
                        style="margin-left: auto; padding: 0"
                        @click="() => handleOpenFinder(true)"
                      >
                        <el-icon size="14"><Fold /></el-icon>
                        View Finder Selection Details
                      </el-button>
                    </div>

                    <!-- Sector -->
                    <div
                      v-if="
                        intakeDisplayScenario?.sectorNodeGuid ||
                        overrideHousingFinderInputs?.sectorNodeGuids?.length
                      "
                      class="compare-label"
                    >
                      Sector
                    </div>
                    <div
                      v-if="
                        intakeDisplayScenario?.sectorNodeGuid ||
                        overrideHousingFinderInputs?.sectorNodeGuids?.length
                      "
                      class="compare-cell compare-col--scenario"
                    >
                      {{
                        intakeDisplayScenario?.sectorNodeGuid
                          ? (getSectorNodeName(
                              intakeDisplayScenario.sectorNodeGuid,
                            ) ?? "—")
                          : "—"
                      }}
                    </div>
                    <div
                      v-if="
                        intakeDisplayScenario?.sectorNodeGuid ||
                        overrideHousingFinderInputs?.sectorNodeGuids?.length
                      "
                      class="compare-cell compare-col--app"
                    >
                      {{
                        overrideHousingFinderInputs?.sectorNodeGuids
                          ?.map((g) => getSectorNodeName(g) ?? g)
                          .join(", ") || "—"
                      }}
                    </div>

                    <!-- Category -->
                    <div
                      v-if="
                        intakeDisplayScenario?.categoryNodeGuid ||
                        overrideHousingFinderInputs?.categoryNodeGuids?.length
                      "
                      class="compare-label"
                    >
                      Category
                    </div>
                    <div
                      v-if="
                        intakeDisplayScenario?.categoryNodeGuid ||
                        overrideHousingFinderInputs?.categoryNodeGuids?.length
                      "
                      class="compare-cell compare-col--scenario"
                    >
                      {{
                        intakeDisplayScenario?.categoryNodeGuid
                          ? (getSectorNodeName(
                              intakeDisplayScenario.categoryNodeGuid,
                            ) ?? "—")
                          : "—"
                      }}
                    </div>
                    <div
                      v-if="
                        intakeDisplayScenario?.categoryNodeGuid ||
                        overrideHousingFinderInputs?.categoryNodeGuids?.length
                      "
                      class="compare-cell compare-col--app"
                    >
                      {{
                        overrideHousingFinderInputs?.categoryNodeGuids
                          ?.map((g) => getSectorNodeName(g) ?? g)
                          .join(", ") || "—"
                      }}
                    </div>

                    <!-- Unit Type -->
                    <div class="compare-label">Unit Type</div>
                    <div class="compare-cell compare-col--scenario">
                      {{
                        (intakeDisplayScenario?.unitTypeLeafNodeGuid
                          ? getUnitTypeNodeName(
                              intakeDisplayScenario.unitTypeLeafNodeGuid,
                            )
                          : undefined) ??
                        intakeOfferFormatUnitType(
                          intakeDisplayScenario?.unitType ?? undefined,
                        )
                      }}
                      <el-tag
                        v-if="intakeDisplayScenario?.scenarioAccessible"
                        size="small"
                        type="warning"
                        style="margin-left: 4px"
                      >
                        Accessible
                      </el-tag>
                    </div>
                    <div class="compare-cell compare-col--app">
                      {{
                        (
                          overrideHousingFinderInputs?.roomPreferenceV2?.map(
                            (g) => getUnitTypeNodeName(g) ?? g,
                          ) ??
                          overrideHousingFinderInputs?.roomPreference ??
                          []
                        ).join(", ") || "—"
                      }}

                      <el-tag
                        v-if="overrideHousingFinderInputs?.accessible"
                        size="small"
                        type="warning"
                        style="margin-left: 4px"
                      >
                        Accessible
                      </el-tag>
                      <el-popover
                        v-if="hasLegacyAccessibleAttr"
                        trigger="hover"
                        placement="top"
                        :width="320"
                      >
                        <template #reference>
                          <el-tag
                            size="small"
                            type="info"
                            style="margin-left: 4px; cursor: help"
                          >
                            Legacy Accessible
                            <el-icon
                              style="margin-left: 2px; vertical-align: middle"
                              ><InfoFilled
                            /></el-icon>
                          </el-tag>
                        </template>
                        <p style="margin: 0">
                          This applicant had the legacy "Accessible Housing"
                          filter selected before it was split into
                          <i>Accessible Building</i> and
                          <i>Accessible Suite</i> options. Please confirm
                          whether they require an accessible suite. If not,
                          remove the accessible suite option via "Application
                          Details" &gt; "Edit in Finder".
                        </p>
                      </el-popover>
                    </div>

                    <!-- Occupancy -->
                    <div class="compare-label">Earliest Occupancy</div>
                    <div class="compare-cell compare-col--scenario">
                      <template v-if="intakeEarliestAvailableDate">
                        {{ formatDate(intakeEarliestAvailableDate, 8) }}
                      </template>
                      <span v-else>—</span>
                    </div>
                    <div class="compare-cell compare-col--app">
                      <template v-if="localHousingApp?.earliestOccupancyDate">
                        {{
                          formatDate(localHousingApp.earliestOccupancyDate, 9)
                        }}
                      </template>
                      <span v-else>—</span>
                    </div>

                    <!-- Building Types (app only) -->
                    <template
                      v-if="overrideHousingFinderInputs?.buildingTypes?.length"
                    >
                      <div class="compare-label">Building Types</div>
                      <div class="compare-cell compare-col--scenario">—</div>
                      <div class="compare-cell compare-col--app">
                        {{
                          overrideHousingFinderInputs.buildingTypes
                            .map((g) => getBuildingTypeName(g) ?? g)
                            .join(", ")
                        }}
                      </div>
                    </template>

                    <!-- Building Attributes (app only) -->
                    <template
                      v-if="
                        overrideHousingFinderInputs?.buildingAttributes?.length
                      "
                    >
                      <div class="compare-label">Attributes</div>
                      <div class="compare-cell compare-col--scenario">—</div>
                      <div class="compare-cell compare-col--app">
                        {{
                          overrideHousingFinderInputs.buildingAttributes
                            .map((g) => getBuildingAttrName(g) ?? g)
                            .filter((n) => n !== "Accessible Housing (Legacy)")
                            .join(", ")
                        }}
                      </div>
                    </template>
                  </div>
                </div>

                <div class="vacancy-mgmt-history">
                  <div class="history-stat-row">
                    <div class="history-stat">
                      <span class="history-stat-label">No Offers</span>
                      <span class="history-stat-value">
                        {{ intakeNoOfferCount }}
                      </span>
                    </div>
                    <div class="history-stat">
                      <span class="history-stat-label">Strikes</span>
                      <span
                        class="history-stat-value"
                        :class="{
                          'strikes-warning': applicationRejectedThreeOffers,
                        }"
                      >
                        {{ intakeStrikesCount }}
                      </span>
                      <el-tag
                        v-if="applicationRejectedThreeOffers"
                        size="small"
                        type="danger"
                      >
                        3+
                      </el-tag>
                    </div>
                    <div class="history-stat">
                      <span class="history-stat-label">Last Activity</span>
                      <span class="history-stat-value">
                        <template v-if="localHousingApp?.dateLastActivity">
                          {{ formatDate(localHousingApp.dateLastActivity) }}
                        </template>
                        <span v-else>—</span>
                      </span>
                    </div>
                    <div class="history-stat">
                      <span class="history-stat-label">Activities</span>
                      <span class="history-stat-value">
                        {{ intakeContactActivityCount }}
                      </span>
                    </div>
                    <div class="history-stat">
                      <span class="history-stat-label">Since Locked</span>
                      <span class="history-stat-value">
                        {{ intakeContactActivitySinceLockedCount }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- ─── Intake Action Panel ─── -->
              <IntakeActionPanel
                v-if="
                  localHousingApp && (reviewFitScenario ?? waitlistScenario)
                "
                :scenario="(reviewFitScenario ?? waitlistScenario)!"
                :entity-guid="localHousingApp.entityGuid ?? ''"
                :application-guid="localHousingApp.guid"
                :processed-status="currentProcessedStatus"
                :application-status="localHousingApp?.status"
                :read-only="
                  !(
                    reviewFitScenario ||
                    isNextReviewFit ||
                    activeIntakeWorkflow ||
                    currentProcessedStatus
                  )
                "
                :concurrency-read-only="effectiveReadOnly"
                :locked-elsewhere="
                  lockedInScenarioEntry?.scenarioGuid !== undefined
                "
                @status-changed="
                  async () => {
                    await handleRefreshHousingApp(localHousingApp!.guid);
                    emit('refetch:reviewFitScenario');
                  }
                "
                @done="handleClose"
                @workflow-counts="handleWorkflowCounts"
              />

              <!-- ─── Intake Workflows ─── -->
              <!-- <div class="application-details-card" style="margin-top: 16px">
                <div>
                  <b>Intake Workflows ({{ intakeWorkflows.length }})</b>
                </div>
                <el-table
                  :data="intakeWorkflows"
                  :default-sort="{ prop: 'linkedAt', order: 'descending' }"
                  style="width: 100%; margin-top: 8px"
                  max-height="250"
                >
                  <el-table-column label="Name" width="400">
                    <template #default="scope">
                      <el-link
                        @click="() => openWorkflow(scope.row.workflowGuid)"
                      >
                        {{ scope.row.workflowName }}
                      </el-link>
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="workflowStatus"
                    label="Status"
                    width="150"
                  >
                    <template #default="scope">
                      {{ splitByCaps(scope.row.workflowStatus) || "-" }}
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="linkedAt"
                    label="Created"
                    width="150"
                    sortable
                  >
                    <template #default="scope">
                      <DateWithTimePopover :date="scope.row.linkedAt" />
                    </template>
                  </el-table-column>
                </el-table>
              </div> -->
            </div>
            <div
              v-else-if="intakeScenarioEntries.length > 0"
              class="intake-offer-section"
            >
              <div
                v-if="activeIntakeScenarioEntries.length > 0"
                class="intake-entries-scroll"
              >
                <div
                  v-for="entry in activeIntakeScenarioEntries"
                  :key="entry.scenarioGuid"
                  class="vacancy-mgmt-card"
                  :class="{
                    'intake-entry--active':
                      entry.status ===
                        HousingIntakeScenarioProcessedApplicationStatus.INTERVIEWING ||
                      entry.status ===
                        HousingIntakeScenarioProcessedApplicationStatus.OFFER_IN_PROGRESS,
                  }"
                >
                  <div class="vacancy-mgmt-header">
                    <span class="vacancy-mgmt-title">{{
                      entry.scenarioName ?? "Scenario"
                    }}</span>
                    <span v-if="entry.siteName" class="vacancy-mgmt-site">{{
                      entry.siteName
                    }}</span>
                    <span v-if="entry.unitType" class="vacancy-mgmt-site">{{
                      intakeOfferFormatUnitType(entry.unitType)
                    }}</span>
                    <EnumTag
                      v-if="entry.status"
                      :model-value="entry.status"
                      size="small"
                      type="housingIntakeScenarioApplicationStatus"
                    />
                    <el-tag v-else size="small" type="info">Waitlist</el-tag>
                  </div>
                </div>
              </div>
              <p
                v-if="activeIntakeScenarioEntries.length > 0"
                class="intake-entries-hint"
                style="
                  margin: 0;
                  font-size: 0.8rem;
                  color: var(--el-text-color-secondary);
                "
              >
                Only active scenarios (Waitlist / Interviewing / Offer in
                Progress / Offer Accepted) are shown above. View completed forms
                below.
              </p>
              <p
                v-else
                class="intake-entries-hint"
                style="
                  margin: 0;
                  font-size: 0.8rem;
                  color: var(--el-text-color-secondary);
                "
              >
                This application is not in any active vacancy scenarios.
              </p>

              <IntakeActionPanel
                v-if="localHousingApp?.entityGuid"
                :fallback-scenario-entry="lockedInScenarioEntry"
                :entity-guid="localHousingApp.entityGuid"
                :application-guid="localHousingApp.guid"
                :application-status="localHousingApp.status"
                :read-only="
                  localHousingApp.status !==
                    HousingApplicationStatus.OFFER_ACCEPTED &&
                  localHousingApp.status !== HousingApplicationStatus.HOUSED
                "
                @status-changed="handleRefreshHousingApp(localHousingApp!.guid)"
                @done="handleClose"
              />
              <p
                v-if="
                  localHousingApp.status !==
                    HousingApplicationStatus.OFFER_ACCEPTED &&
                  localHousingApp.status !== HousingApplicationStatus.HOUSED
                "
                class="intake-entries-hint"
              >
                Open from
                <el-link
                  type="primary"
                  @click="
                    router.push(
                      `/portal/process/${applicationRouteMap.HMB.children.Process.children.IntakeOffer.path}`,
                    )
                  "
                >
                  Vacancy Management </el-link
                >to take intake actions.
              </p>
            </div>
            <div v-else class="info-msg">
              View
              <el-link
                @click="
                  router.push(
                    `/portal/process/${applicationRouteMap.HMB.children.Process.children.IntakeOffer.path}`,
                  )
                "
              >
                Vacancy Management
              </el-link>
              page to see actions.
            </div>
          </el-collapse-item>

          <el-collapse-item
            title="History of Events"
            :name="ApplicationDrawerTabs.TIMELINE"
            :class="{
              'highlighted-section': originalSectionsOpened.includes(
                ApplicationDrawerTabs.TIMELINE,
              ),
            }"
            @click="handleHistoryOfEventsShow"
          >
            <el-table
              v-if="timeline.length"
              class="historyTable"
              :data="timeline"
              stripe
              style="width: 100%"
              height="80vh"
            >
              <el-table-column label="Event" min-width="80">
                <template #default="{ row }: { row: HousingApplicationLogDto }">
                  {{ splitByCaps(row.activityCode) }}
                </template>
              </el-table-column>
              <el-table-column label="Details" min-width="400">
                <template #default="{ row }: { row: HousingApplicationLogDto }">
                  <div style="white-space: pre-wrap">{{ row.note }}</div>
                </template>
              </el-table-column>
              <el-table-column label="When" width="250">
                <template #default="{ row }: { row: HousingApplicationLogDto }">
                  {{ formatDate(row.dateCreated, 7) }}
                </template>
              </el-table-column>
              <el-table-column label="By" width="180">
                <template #default="{ row }: { row: HousingApplicationLogDto }">
                  {{ row.who }}
                </template>
              </el-table-column>
            </el-table>
            <div v-else style="padding: 1em">
              <span v-if="timelineLoaded">No events to show.</span>
              <div v-else>
                <span>Timeline could not be shown yet.</span>
                <el-button
                  size="small"
                  style="margin-left: 0.75em"
                  @click.stop="handleHistoryOfEventsShow"
                >
                  Retry loading timeline
                </el-button>
              </div>
            </div>
          </el-collapse-item>

          <el-collapse-item
            v-if="isViteDevelopment"
            title="Development Only"
            name="Dev"
          >
            <div>
              <el-button @click="getAnswersFromHousingApplication"
                >Get Answers</el-button
              >
            </div>
            <div class="manuallySetStatus">
              <span>Manually Set... Status: </span>
              <el-select v-model="localHousingApp.status">
                <el-option
                  v-for="[value, label] of Object.entries(
                    housingApplicationStatusDisplayNamesHMBSite,
                  ).filter(([, label]) => label !== null)"
                  :key="value"
                  :label="label!"
                  :value="value"
                />
              </el-select>
              <el-button size="small" @click="setStatus(localHousingApp.status)"
                >Set Status</el-button
              >

              <span>Needs Assessment Score</span>
              <el-input-number
                :model-value="localHousingApp.needsAssessmentScore ?? undefined"
                :step="5"
                step-strictly
                :max="80"
                :min="0"
                @change="localHousingApp.needsAssessmentScore = $event"
              />
              <el-button size="small" @click="setScore">Set Score</el-button>
            </div>

            <div class="dumpInfo">
              <h2>Housing Application dump</h2>
              {{ dumpInfo }}
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>
  </el-drawer>

  <!-- Contact Detail Drawer -->
  <el-drawer
    v-model="contactDrawerVisible"
    :title="selectedContact?.name || 'Contact Details'"
    size="70%"
    @close="selectedContact = null"
  >
    <CRMEntityDetail
      v-if="
        selectedContact?.entityDefinitionGuid && selectedContact?.entityGuid
      "
      :entity-definition-guid="selectedContact.entityDefinitionGuid"
      :entity-guid="selectedContact.entityGuid"
      :is-drawer="true"
    />
  </el-drawer>

  <HousingApplicationFinderViewer
    v-if="housingApp?.guid"
    v-model="finderDrawerOpen"
    :application-guid="housingApp.guid"
    :is-read-only="finderDrawerReadOnly"
    @closed="housingApp?.guid && handleRefreshHousingApp(housingApp.guid)"
  />

  <!-- Workflow Detail Viewer (rendered outside drawer) -->
  <WorkflowResponseViewer
    v-if="selectedWorkflow"
    :compliance-workflow="selectedWorkflow"
    :full-screen="false"
    :focus-form-response-guid="selectedWorkflowFocusFormResponseGuid"
    @close-drawer="handleWorkflowDrawerClose"
    @update-workflow="handleWorkflowUpdated"
    @refresh-list="handleWorkflowUpdated"
    @open-follow-up-workflow="handleOpenFollowUpWorkflow"
  >
    <template
      v-if="
        selectedWorkflow.workflowDefinitionName === 'Housing Application' ||
        selectedWorkflow.workflowDefinitionName ===
          'Legacy: Imported Information'
      "
      #sidebar-footer
    >
      <ApplicationLocations
        :finder-inputs="overrideHousingFinderInputs"
        :locations-selected="locationsSelectedOverride"
        :single-column="true"
        class="finder-info"
      />
    </template>
  </WorkflowResponseViewer>
</template>
<style lang="less">
.app-editor-banner {
  position: sticky;
  top: 0;
  z-index: 10;
  padding-bottom: 1em;

  .el-alert {
    width: fit-content;
    margin: 0 auto;
    font-size: 1rem;
  }

  .el-button {
    margin-left: 1em;
  }
}

.app-viewers-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  position: sticky;
  top: 0;
  z-index: 10;
  padding-bottom: 1em;

  .el-alert {
    width: fit-content;
  }
}

.el-table.historyTable {
  margin-bottom: 30px;
  .el-table__cell {
    vertical-align: top;
  }
}

.app-idle-banner {
  position: sticky;
  top: 0;
  z-index: 11;
  padding-bottom: 0.5em;

  .el-alert {
    .el-button {
      margin-left: 1em;
    }
  }
}

.housing-application-drawer {
  display: flex;
  justify-content: space-between;
  padding-bottom: 1rem;

  .application-summary {
    display: grid;
    grid-template-columns: repeat(2, min-content);
    gap: 3em;
    align-items: start;

    .summary-info {
      display: flex;
      flex-direction: column;
      gap: 0.5em;
      flex-wrap: wrap;
      .summary-info-row {
        display: flex;
        align-items: center;
        gap: 1em;
        flex-wrap: wrap;
        text-wrap: nowrap;

        .el-select__wrapper.is-filterable {
          min-width: 200px;
        }
      }
      .assign-to {
        flex-wrap: nowrap;
      }
    }

    .summary-contacts-box {
      border: 1.5px solid var(--el-border-color);
      border-radius: var(--el-border-radius-base);
      overflow: hidden;
      min-width: 400px;

      .set-primary-btn {
        width: 2em;
        margin-right: 1em;
      }

      .primary-star {
        color: var(--el-color-warning);
        font-size: 14px;
      }

      .no-contacts-fallback {
        display: flex;
        flex-direction: column;
        gap: 0.5em;
        padding: 12px 14px;
      }

      .contact-instructions {
        margin-top: 8px;
        padding: 8px 14px;
        background-color: var(--el-fill-color-light);
        border: 1px solid var(--el-border-color);

        &.has-content {
          background-color: #fef9e7;
          border-top: 1px solid #f5e6a3;
        }

        .contact-instructions-inner {
          font-size: 12px;

          strong {
            color: var(--el-text-color-secondary);
          }
        }

        &.has-content .contact-instructions-inner {
          color: #7c6a14;
          strong {
            color: #92730a;
          }
        }

        .contact-info-edit {
          margin-top: 4px;
          display: flex;
          gap: 6px;
          align-items: flex-start;
          width: 50%;
        }
      }

      :deep(.primary-contact-row) {
        background-color: #fffbeb !important;
        td {
          background-color: inherit !important;
        }
      }

      .star-icon {
        color: #e6a23c;
        font-size: 14px;
      }

      .editable-contact-info {
        cursor: pointer;
        padding: 2px 4px;
        border-radius: 3px;
        margin-left: 4px;

        &:hover {
          background-color: var(--el-fill-color);
        }
      }
    }
  }

  .summary-buttons {
    margin-top: 0.75em;

    .summary-buttons-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5em;
    }
  }

  .on-hold-due {
    margin-left: 0.5rem;
    font-size: 0.95rem;
    color: var(--el-text-color-regular);
    display: inline-flex;
    align-items: center;
  }

  .on-hold-due__icon {
    margin-right: 0.35rem;
    color: var(--el-color-primary);
  }

  .on-hold-due__date {
    font-weight: 500;
    margin: 0.25rem;
  }

  .on-hold-due__relative {
    font-size: 0.85rem;
    color: var(--el-text-color-secondary);
  }

  .intake-offer-accepted-actions {
    display: flex;
    gap: 1em;

    .update-occupancy {
      display: flex;
      flex-direction: column;
      gap: 0.5em;
    }
  }

  .intake-entries-scroll {
    max-height: 240px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .intake-offer-section {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .formHost {
      .form {
        background: white;
      }
    }

    .form-title {
      font-weight: 600;
      color: var(--el-text-color-regular);
    }

    .workflow-inline {
      background: var(--el-bg-color-page);
    }

    .el-collapse-item__wrap {
      background: transparent;
    }
  }

  .vacancy-mgmt-card {
    background: var(--el-bg-color-page);
    border: 3px solid var(--el-border-color-lighter);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 0;

    .vacancy-mgmt-header {
      padding: 8px 12px;
      background: var(--el-color-primary-light-9);
      border-bottom: 1px solid var(--el-border-color-lighter);
      display: flex;
      align-items: center;
      gap: 6px;

      .vacancy-mgmt-title {
        font-weight: 600;
        font-size: 0.875rem;
        color: var(--el-text-color-primary);
      }

      .vacancy-mgmt-site {
        font-size: 0.8rem;
        color: var(--el-text-color-secondary);
        &::before {
          content: "/";
          margin-right: 6px;
          color: var(--el-text-color-placeholder);
        }
      }
    }

    .vacancy-months-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      flex-wrap: wrap;

      .vacancy-months-boxes {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        flex: 1;

        .month-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 3px 8px;
          background: white;
          border: 1px solid var(--el-border-color-extra-light);
          border-radius: 4px;
          min-width: 48px;

          .month-value {
            font-size: 0.85rem;
            font-weight: 700;
            color: var(--el-text-color-primary);
            font-variant-numeric: tabular-nums;
          }

          .month-label {
            font-size: 0.55rem;
            color: var(--el-text-color-secondary);
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }

          &.total-box {
            background: var(--el-color-primary-light-9);
            border-color: var(--el-color-primary-light-7);
          }
        }

        .details-toggle-btn {
          align-self: center;
          flex-shrink: 0;
        }
      }
    }

    .vacancy-mgmt-compare {
      padding: 8px 12px;
      border-top: 1px solid var(--el-border-color-extra-light);

      .compare-grid {
        display: grid;
        grid-template-columns: auto 1fr 1fr;
        gap: 0;
        border: 1px solid var(--el-border-color-lighter);
        border-radius: 4px;
        overflow: hidden;
        font-size: 0.8rem;
        max-width: 700px;
      }

      .compare-col-header {
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 4px 8px;
        border-bottom: 1px solid var(--el-border-color-lighter);
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .compare-col--scenario {
        background: var(--el-color-primary-light-9);
        color: var(--el-color-primary);
      }

      .compare-col--app {
        background: var(--el-color-success-light-9);
        color: var(--el-color-success);
      }

      .compare-label {
        font-size: 0.7rem;
        font-weight: 500;
        color: var(--el-text-color-secondary);
        text-transform: uppercase;
        letter-spacing: 0.02em;
        padding: 4px 8px;
        background: var(--el-fill-color-lighter);
        border-bottom: 1px solid var(--el-border-color-extra-light);
        display: flex;
        align-items: center;
      }

      .compare-cell {
        padding: 4px 8px;
        color: var(--el-text-color-primary);
        border-bottom: 1px solid var(--el-border-color-extra-light);
        display: flex;
        align-items: center;
        gap: 4px;
        font-weight: 500;
      }

      .compare-col--scenario.compare-cell {
        background: var(--el-bg-color);
      }

      .compare-col--app.compare-cell {
        background: var(--el-bg-color);
      }

      .compare-grid > :nth-last-child(-n + 3) {
        border-bottom: none;
      }
    }

    .vacancy-mgmt-table {
      margin: 0 12px;
      width: calc(100% - 24px);
    }

    .vacancy-mgmt-history {
      padding: 8px 12px;
      border-top: 1px solid var(--el-border-color-extra-light);
      margin-top: 8px;

      .history-stat-row {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .history-stat {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: var(--el-bg-color);
        border: 1px solid var(--el-border-color-extra-light);
        border-radius: 4px;
        font-size: 0.8rem;

        .history-stat-label {
          color: var(--el-text-color-secondary);
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .history-stat-value {
          font-weight: 600;
          color: var(--el-text-color-primary);
          font-size: 0.85rem;
        }
      }
    }

    > .el-button {
      align-self: flex-start;
      margin: 4px 12px 8px;
    }
  }

  .intake-entry--active {
    border-color: var(--el-color-primary-light-5);
    background: var(--el-color-primary-light-9);
  }

  .intake-entries-hint {
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: var(--el-text-color-secondary);
    display: flex;
    align-items: flex-end;
    gap: 0.5em;
  }

  .strikes-warning {
    color: var(--el-color-danger) !important;
    font-weight: 600;
  }

  .intake-action-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding-top: 4px;
  }

  .intake-workflow-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    color: var(--el-text-color-secondary);
    font-size: 0.85rem;
  }

  .el-drawer__header {
    margin-bottom: 0rem;

    .el-drawer__title {
      padding-left: 0.3rem;
      font-size: 2rem;
      color: var(--el-color-black);
      font-weight: 400;
    }
  }

  .el-collapse-item__header {
    font-size: 1rem;
    font-weight: 500;
    border-radius: 10px;
    padding: 0.3rem 1rem;
    background-color: rgba(229, 229, 229, 0.598);
    box-shadow: inset 0 -1.5px 0 rgba(0, 0, 0, 0.1);
    line-height: normal;
    height: fit-content;
  }

  .highlighted-section {
    &.is-active {
      .el-collapse-item__header {
        background-color: #bbe8b9;
      }
    }
  }

  .el-collapse-item {
    &.is-active {
      &:not(.highlighted-section) {
        .el-collapse-item__header {
          background-color: rgba(125, 170, 249, 0.214);
        }
      }
    }
  }

  .application-details-card {
    background-color: var(--el-bg-color-page);
    border-radius: var(--el-border-radius-base);
    border: var(--el-border);
    padding: 4px 8px;
    width: calc(100% - 8px);

    .overflow-text {
      display: inline;
      width: 250px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-decoration: underline;
      font-weight: 500;
      cursor: pointer;
    }
  }
  .application-details {
    display: flex;
    flex-direction: column;
    gap: 1em;
    flex-wrap: wrap;

    .table,
    .finder-info {
      margin-top: 8px;
      flex-grow: 1;
    }

    .table {
      max-width: 100%;
    }
    .notes-section {
      background-color: var(--el-bg-color-page);
      border-radius: var(--el-border-radius-base);
      border: var(--el-border);
      padding: 4px 8px;
      width: calc(100% - 8px);

      .sub-header {
        font-weight: 600;
        padding-right: 0.3rem;
        margin-bottom: 100px;
      }

      .el-table .new-row {
        background-color: var(--el-color-primary-light-9);
      }

      .el-table--fit {
        margin-top: 5px;
      }

      .note {
        width: 100%;
      }
    }
  }

  .el-drawer__body {
    padding-top: 0;
    .el-collapse-item__content {
      font-size: 0.9rem;
      padding: 1rem 1rem 1.5rem 1rem;

      .header {
        font-weight: 600;
        font-size: 1rem;
      }
      .sub-header {
        font-weight: 600;
        padding-right: 0.3rem;
        text-wrap: nowrap;
      }

      .el-link {
        display: block;
        .el-link__inner {
          font-size: 0.9rem;
          text-decoration: underline;
        }
      }

      .info-msg {
        margin: 1em 0;
        padding: 1em;
        background-color: var(--el-color-warning-light-9);

        &.three-rejections {
          background-color: var(--el-color-danger-light-7);
        }
        .el-link {
          display: inline;
        }
      }
      .qualified-locations {
        margin-top: 8px;
      }
    }
  }

  .timeline {
    margin-top: 2em;
  }

  .dumpInfo {
    white-space: pre-wrap;
    max-height: 30vh;
    padding: 2em;
    overflow: auto;
    background-color: var(--el-color-info-light-9);
  }
  .manuallySetStatus {
    display: flex;
    align-items: center;
    gap: 1em;
    .el-select {
      width: 200px;
    }
  }
  .checklist {
    display: flex;
    flex-direction: column;
    .step-completed-by {
      color: var(--el-text-color-regular);
      font-size: 0.9em;
    }
  }
}
</style>
